import store from "@/state/store";
import { canView } from "@/helpers/permissions";

/**
 * Maps a configurable default-dashboard path → the form/permission key the route
 * is gated by. Used by the `/` default-redirect to fall back to a dashboard the
 * user can actually view if the configured one is permission-denied.
 */
const DASHBOARD_PERM_BY_PATH = {
  "/dashboard/erp": "dashboard",
  "/dashboard/store": "dashboard-store",
  "/erp/tp/dashboard/admin": "tp-dashboard",
  "/erp/tp/dashboard/fleet": "tp-dashboard",
  "/erp/jc/dashboard": "jc-dashboard",
  "/erp/mm/dashboard": "mm-dashboard",
};

/**
 * Resolves the post-login / sidebar-Dashboard target. Reads the per-company
 * configured path from the accountSettings store; if the user lacks the matching
 * permission, walks the dashboard list to find the first one they can view.
 * Final fallback is /dashboard/erp (legacy behaviour).
 */
export function resolveDefaultDashboardPath() {
  const configured = store.getters["accountSettings/defaultDashboardPath"];
  const requiredPerm = DASHBOARD_PERM_BY_PATH[configured];
  if (!requiredPerm || canView(requiredPerm)) return configured;
  const fallback = Object.entries(DASHBOARD_PERM_BY_PATH).find(([, p]) => canView(p));
  return fallback ? fallback[0] : "/dashboard/erp";
}

export default [
  {
    // Public share page — no auth required. Anyone with a valid, unexpired
    // token can view the rendered resource. The component fetches via
    // /api/public/share/{token} which strips Authorization/X-Company-Id
    // headers (see api.js interceptor).
    path: "/share/:token",
    name: "PublicShare",
    component: () => import("../views/public/PublicShare.vue"),
    meta: { title: "Shared Document" },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("../views/account/login.vue"),
    meta: {
      title: "Login",
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters["auth/loggedIn"]) {
          // Redirect to the home page instead
          next({ name: "default" });
          next();
        } else {
          // Continue to the login page
          next();
        }
      },
    },
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("../views/account/register.vue"),
    meta: {
      title: "Register",
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters["auth/loggedIn"]) {
          // Redirect to the home page instead
          next({ name: "default" });
        } else {
          // Continue to the login page
          next();
        }
      },
    },
  },
  {
    path: "/forgot-password",
    name: "Forgot password",
    component: () => import("../views/account/forgot-password.vue"),
    meta: {
      title: "Forgot Password",
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters["auth/loggedIn"]) {
          // Redirect to the home page instead
          next({ name: "default" });
        } else {
          // Continue to the login page
          next();
        }
      },
    },
  },
  {
    path: "/",
    name: "default",
    meta: {
      title: "Dashboard",
      authRequired: true,
      workspaceTab: false,
    },
    redirect: () => resolveDefaultDashboardPath(),
  },
  {
    path: "/dashboard/erp",
    name: "dashboard-erp",
    meta: {
      title: "ERP Dashboard",
      authRequired: true,
    },
    component: () => import("../views/erp/dashboard/Index.vue"),
  },
  {
    path: "/dashboard/store",
    name: "dashboard-store",
    meta: {
      title: "Store Dashboard",
      authRequired: true,
    },
    component: () => import("../views/erp/store/dashboard/Index.vue"),
  },
  {
    path: "/erp/chatbot",
    name: "erp-chatbot",
    meta: {
      title: "ERP Assistant",
      authRequired: true,
    },
    component: () => import("../views/erp/chatbot/Index.vue"),
  },
  {
    path: "/erp/scheduled-reports",
    name: "erp-scheduled-reports",
    meta: { title: "Scheduled Reports", authRequired: true },
    component: () => import("../views/erp/scheduled-reports/Index.vue"),
  },
  {
    path: "/erp/chat",
    name: "erp-chat",
    meta: { title: "Chat", authRequired: true },
    component: () => import("../views/erp/chat/Index.vue"),
  },
  {
    path: "/erp/chat/:conversationId",
    name: "erp-chat-conversation",
    meta: { title: "Chat", authRequired: true },
    component: () => import("../views/erp/chat/Index.vue"),
  },
  {
    path: "/erp/insights",
    name: "erp-insights",
    meta: {
      title: "AI Insights",
      authRequired: true,
    },
    component: () => import("../views/erp/insights/Index.vue"),
  },
  {
    path: "/dashboard/crypto",
    name: "dashboard-crypto",
    meta: {
      title: "Crypto",
      authRequired: true,
    },
    component: () => import("../views/dashboard/crypto/index"),
  },
  {
    path: "/dashboard/projects",
    name: "dashboard-projects",
    meta: {
      title: "Projects", authRequired: true,
    },
    component: () => import("../views/dashboard/projects/index"),
  },
  {
    path: "/dashboard/crm",
    name: "dashboard-crm",
    meta: {
      title: "CRM", authRequired: true,
    },
    component: () => import("../views/dashboard/crm/index"),
  },
  {
    path: "/dashboard/analytics",
    name: "dashboard-analytics",
    meta: {
      title: "Analytics", authRequired: true,
    },
    component: () => import("../views/dashboard/analytics/index"),
  },
  {
    path: "/dashboard/nft",
    name: "dashboard-nft",
    meta: {
      title: "NFT Dashboard", authRequired: true,
    },
    component: () => import("../views/dashboard/nft/index"),
  },
  {
    path: "/dashboard/job",
    name: "dashboard-job",
    meta: {
      title: "Job Dashboard", authRequired: true,
    },
    component: () => import("../views/dashboard/job/index"),
  },
  {
    path: "/dashboard/blog",
    name: "dashboard-blog",
    meta: {
      title: "Blog Dashboard", authRequired: true,
    },
    component: () => import("../views/dashboard/blog/index"),
  },
  {
    path: "/logout",
    name: "logout",
    meta: {
      title: "Logout", authRequired: true, workspaceTab: false,
      beforeResolve(routeTo, routeFrom, next) {
        localStorage.clear();
        sessionStorage.clear();
        next();
      },
    },
    component: () => import("../views/auth/logout/basic")
  },
  {
    path: "/main-calendar",
    name: "main-calendar",
    meta: { title: "Calendar", authRequired: true },
    component: () => import("../views/calendar/main"),
  },
  {
    path: "/month-grid",
    name: "month-grid",
    meta: { title: "Month Grid", authRequired: true },
    component: () => import("../views/calendar/month"),
  },
  {
    path: "/chat",
    name: "chat",
    meta: { title: "Chat", authRequired: true },
    component: () => import("../views/apps/chat"),
  },
  {
    path: "/apps-file-manager",
    name: "file manager",
    meta: { title: "File Manager", authRequired: true },
    component: () => import("../views/apps/filemanager"),
  },
  {
    path: "/apps-todo",
    name: "To Do List",
    meta: { title: "To Do List", authRequired: true },
    component: () => import("../views/apps/todo"),
  },
  {
    path: "/mailbox",
    name: "mailbox",
    meta: { title: "Mailbox", authRequired: true },
    component: () => import("../views/apps/email/mailbox"),
  },
  {
    path: "/email/email-basic",
    name: "email-basic",
    meta: { title: "Basic Action", authRequired: true },
    component: () => import("../views/apps/email/email-basic"),
  },
  {
    path: "/email/email-ecommerce",
    name: "email-ecommerce",
    meta: { title: "Ecommerce Action", authRequired: true },
    component: () => import("../views/apps/email/email-ecommerce"),
  },
  {
    path: "/apps/nft-auction",
    name: "auction",
    meta: { title: "Live Auction", authRequired: true },
    component: () => import("../views/apps/nft/nft-auction"),
  },
  {
    path: "/apps/nft-collection",
    name: "collection",
    meta: { title: "Collactions", authRequired: true },
    component: () => import("../views/apps/nft/nft-collection"),
  },
  {
    path: "/apps/nft-create",
    name: "create",
    meta: { title: "Create", authRequired: true },
    component: () => import("../views/apps/nft/nft-create"),
  },
  {
    path: "/apps/nft-creators",
    name: "creators",
    meta: { title: "Creators", authRequired: true },
    component: () => import("../views/apps/nft/nft-creators"),
  },
  {
    path: "/apps/nft-explore",
    name: "explore",
    meta: { title: "Explore Now", authRequired: true },
    component: () => import("../views/apps/nft/nft-explore"),
  },
  {
    path: "/apps/nft-item-detail",
    name: "item-detail",
    meta: { title: "Item Detail", authRequired: true },
    component: () => import("../views/apps/nft/nft-item-detail"),
  },
  {
    path: "/apps/nft-marketplace",
    name: "marketplace",
    meta: { title: "Marketplace", authRequired: true },
    component: () => import("../views/apps/nft/nft-marketplace"),
  },
  {
    path: "/apps/nft-ranking",
    name: "ranking",
    meta: { title: "Ranking", authRequired: true },
    component: () => import("../views/apps/nft/nft-ranking"),
  },
  {
    path: "/apps/nft-wallet",
    name: "nft-wallet",
    meta: { title: "Wallet", authRequired: true },
    component: () => import("../views/apps/nft/nft-wallet"),
  },
  {
    path: "/apps/projects-list",
    name: "projects-list",
    meta: { title: "Projects List", authRequired: true },
    component: () => import("../views/apps/projects/list"),
  },
  {
    path: "/apps/projects-overview",
    name: "projects Overview",
    meta: { title: "Projects Overview", authRequired: true },
    component: () => import("../views/apps/projects/overview"),
  },
  {
    path: "/apps/projects-create",
    name: "projects-create",
    meta: { title: "Projects Create", authRequired: true },
    component: () => import("../views/apps/projects/create"),
  },
  {
    path: "/apps/tasks-details",
    name: "tasks-details",
    meta: { title: "Tasks Details", authRequired: true },
    component: () => import("../views/apps/tasks/details"),
  },
  {
    path: "/apps/tasks-kanban",
    name: "tasks-kanban",
    meta: { title: "Tasks Kanban", authRequired: true },
    component: () => import("../views/apps/tasks/kanban"),
  },
  {
    path: "/apps/tasks-list-view",
    name: "tasks-list-view",
    meta: { title: "Taks List", authRequired: true },
    component: () => import("../views/apps/tasks/list-view"),
  },
  {
    path: "/apps/tickets-details",
    name: "tickets-details",
    meta: { title: "Ticket Details", authRequired: true },
    component: () => import("../views/tickets/details"),
  },
  {
    path: "/apps/tickets-list",
    name: "tickets-list",
    meta: { title: "Tickets List", authRequired: true },
    component: () => import("../views/tickets/list"),
  },
  {
    path: "/apps/crm-contacts",
    name: "crm-contacts",
    meta: { title: "CRM Contacts", authRequired: true },
    component: () => import("../views/apps/crm/contacts"),
  },
  {
    path: "/apps/crm-companies",
    name: "crm-companies",
    meta: { title: "CRM companies", authRequired: true },
    component: () => import("../views/apps/crm/companies"),
  },
  {
    path: "/apps/crm-deals",
    name: "crm-deals",
    meta: { title: "CRM Deals", authRequired: true },
    component: () => import("../views/apps/crm/deals"),
  },
  {
    path: "/apps/crm-leads",
    name: "crm-leads",
    meta: { title: "CRM Leads", authRequired: true },
    component: () => import("../views/apps/crm/leads"),
  },
  {
    path: "/ecommerce/customers",
    name: "customers",
    meta: { title: "Customers", authRequired: true },
    component: () => import("../views/apps/ecommerce/customers"),
  },
  {
    path: "/ecommerce/products",
    name: "products",
    meta: { title: "Products", authRequired: true },
    component: () => import("../views/apps/ecommerce/products"),
  },
  {
    path: "/ecommerce/product-details",
    name: "product-detail",
    meta: { title: "Product Details", authRequired: true },
    component: () => import("../views/apps/ecommerce/product-details"),
  },
  {
    path: "/ecommerce/orders",
    name: "orders",
    meta: { title: "Orders", authRequired: true },
    component: () => import("../views/apps/ecommerce/orders"),
  },
  {
    path: "/ecommerce/order-details",
    name: "order-details",
    meta: { title: "Order Details", authRequired: true },
    component: () => import("../views/apps/ecommerce/order-details"),
  },
  {
    path: "/ecommerce/add-product",
    name: "add-product",
    meta: { title: "Create Product", authRequired: true },
    component: () => import("../views/apps/ecommerce/add-product"),
  },
  {
    path: "/ecommerce/shopping-cart",
    name: "shopping cart",
    meta: { title: "Shopping Cart", authRequired: true },
    component: () => import("../views/apps/ecommerce/shopping-cart"),
  },
  {
    path: "/ecommerce/checkout",
    name: "checkout",
    meta: { title: "Check Out", authRequired: true },
    component: () => import("../views/apps/ecommerce/checkout"),
  },
  {
    path: "/ecommerce/sellers",
    name: "sellers",
    meta: { title: "Sellers", authRequired: true },
    component: () => import("../views/apps/ecommerce/sellers"),
  },
  {
    path: "/ecommerce/seller-details",
    name: "seller-details",
    meta: { title: "Seller Details", authRequired: true },
    component: () => import("../views/apps/ecommerce/seller-details"),
  },
  {
    path: "/crypto/buy-sell",
    name: "buy/sell",
    meta: { title: "Buy & Sell", authRequired: true },
    component: () => import("../views/apps/crypto/buy-sell"),
  },
  {
    path: "/crypto/kyc",
    name: "kyc",
    meta: { title: "KYC Application", authRequired: true },
    component: () => import("../views/apps/crypto/kyc"),
  },
  {
    path: "/crypto/ico",
    name: "ico",
    meta: { title: "ICO List", authRequired: true },
    component: () => import("../views/apps/crypto/ico"),
  },
  {
    path: "/crypto/orders",
    name: "crypto-orders",
    meta: { title: "Orders", authRequired: true },
    component: () => import("../views/apps/crypto/orders"),
  },
  {
    path: "/crypto/wallet",
    name: "wallet",
    meta: { title: "My Wallet", authRequired: true },
    component: () => import("../views/apps/crypto/wallet"),
  },
  {
    path: "/crypto/transactions",
    name: "transactions",
    meta: { title: "Transactions", authRequired: true },
    component: () => import("../views/apps/crypto/transactions"),
  },
  {
    path: "/invoices/detail/:id?",
    name: "invoice-detail",
    meta: { title: "Invoice Details", authRequired: true },
    component: () => import("../views/apps/invoices/details"),
  },
  {
    path: "/invoices/list",
    name: "invoice-list",
    meta: { title: "Invoice List", authRequired: true },
    component: () => import("../views/apps/invoices/list"),
  },
  {
    path: "/invoices/create",
    name: "invoice-create",
    meta: { title: "Create Invoice", authRequired: true },
    component: () => import("../views/apps/invoices/create"),
  },
  {
    path: "/jobs/application",
    name: "job-application",
    meta: { title: "Job Application", authRequired: true },
    component: () => import("../views/apps/jobs/application"),
  },
  {
    path: "/jobs/candidate-grid",
    name: "job-candidate-grid",
    meta: { title: "Candidate Grid", authRequired: true },
    component: () => import("../views/apps/jobs/candidate-grid"),
  },
  {
    path: "/jobs/candidate-lists",
    name: "job-candidate-lists",
    meta: { title: "Candidate Lists", authRequired: true },
    component: () => import("../views/apps/jobs/candidate-lists"),
  },
  {
    path: "/jobs/categories",
    name: "job-categories",
    meta: { title: "Job Categories", authRequired: true },
    component: () => import("../views/apps/jobs/categories"),
  },
  {
    path: "/jobs/companies-list",
    name: "job-companies-list",
    meta: { title: "Companies List", authRequired: true },
    component: () => import("../views/apps/jobs/companies-list"),
  },
  {
    path: "/jobs/details",
    name: "job-details",
    meta: { title: "Job Details", authRequired: true },
    component: () => import("../views/apps/jobs/details"),
  },
  {
    path: "/jobs/grid-lists",
    name: "job-grid-lists",
    meta: { title: "Grid Lists", authRequired: true },
    component: () => import("../views/apps/jobs/grid-lists"),
  },
  {
    path: "/jobs/lists",
    name: "job-lists",
    meta: { title: "Job Lists", authRequired: true },
    component: () => import("../views/apps/jobs/lists"),
  },
  {
    path: "/jobs/new",
    name: "job-new",
    meta: { title: "New Job", authRequired: true },
    component: () => import("../views/apps/jobs/new"),
  },
  {
    path: "/jobs/statistics",
    name: "job-statistics",
    meta: { title: "Job Statistics", authRequired: true },
    component: () => import("../views/apps/jobs/statistics"),
  },
  {
    path: "/apps-api-key",
    name: "API Key",
    meta: { title: "API Key", authRequired: true },
    component: () => import("../views/apps/apikey"),
  },

  {
    path: "/ui/alerts",
    name: "Alerts",
    meta: {
      title: "Alerts", authRequired: true,
    },
    component: () => import("../views/ui/alerts"),
  },
  {
    path: "/ui/buttons",
    name: "Buttons",
    meta: {
      title: "Buttons", authRequired: true,
    },
    component: () => import("../views/ui/buttons"),
  },
  {
    path: "/ui/cards",
    name: "Cards",
    meta: { title: "Cards", authRequired: true },
    component: () => import("../views/ui/cards"),
  },
  {
    path: "/ui/carousel",
    name: "Carousel",
    meta: { title: "Carousel", authRequired: true },
    component: () => import("../views/ui/carousel"),
  },
  {
    path: "/ui/dropdowns",
    name: "Dropdowns",
    meta: { title: "Dropdowns", authRequired: true },
    component: () => import("../views/ui/dropdowns"),
  },
  {
    path: "/ui/grid",
    name: "Grid",
    meta: { title: "Grid", authRequired: true },
    component: () => import("../views/ui/grid"),
  },
  {
    path: "/ui/images",
    name: "Images",
    meta: { title: "Images", authRequired: true },
    component: () => import("../views/ui/images"),
  },
  {
    path: "/ui/modals",
    name: "Modals",
    meta: { title: "Modals", authRequired: true },
    component: () => import("../views/ui/modals"),
  },
  {
    path: "/ui/offcanvas",
    name: "offcanvas",
    meta: { title: "Offcanvas", authRequired: true },
    component: () => import("../views/ui/offcanvas"),
  },
  {
    path: "/ui/progress",
    name: "Progress",
    meta: { title: "Progress", authRequired: true },
    component: () => import("../views/ui/progress"),
  },
  {
    path: "/ui/placeholders",
    name: "Placeholders",
    meta: { title: "Placeholders", authRequired: true },
    component: () => import("../views/ui/placeholders"),
  },
  {
    path: "/ui/accordions",
    name: "accordions",
    meta: { title: "Accordions", authRequired: true },
    component: () => import("../views/ui/accordions"),
  },
  {
    path: "/ui/tabs",
    name: "tabs",
    meta: { title: "Tabs", authRequired: true },
    component: () => import("../views/ui/tabs"),
  },
  {
    path: "/ui/typography",
    name: "Typography",
    meta: { title: "Typography", authRequired: true },
    component: () => import("../views/ui/typography"),
  },
  {
    path: "/ui/embed-video",
    name: "embed-video",
    meta: { title: "Embed Video", authRequired: true },
    component: () => import("../views/ui/embed-video"),
  },
  {
    path: "/ui/ribbons",
    name: "ribbons",
    meta: { title: "Ribbons", authRequired: true },
    component: () => import("../views/ui/ribbons"),
  },
  {
    path: "/ui/lists",
    name: "lists",
    meta: { title: "Lists", authRequired: true },
    component: () => import("../views/ui/lists"),
  },
  {
    path: "/ui/links",
    name: "links",
    meta: { title: "Links", authRequired: true },
    component: () => import("../views/ui/links"),
  },
  {
    path: "/ui/utilities",
    name: "utilities",
    meta: { title: "Utilities", authRequired: true },
    component: () => import("../views/ui/utilities"),
  },
  {
    path: "/ui/notifications",
    name: "notifications",
    meta: { title: "Notifications", authRequired: true },
    component: () => import("../views/ui/notifications"),
  },
  {
    path: "/ui/general",
    name: "General",
    meta: { title: "General", authRequired: true },
    component: () => import("../views/ui/general"),
  },
  {
    path: "/ui/colors",
    name: "Colors",
    meta: { title: "Colors", authRequired: true },
    component: () => import("../views/ui/colors"),
  },
  {
    path: "/ui/badges",
    name: "badges",
    meta: { title: "Badges", authRequired: true },
    component: () => import("../views/ui/badges"),
  },
  {
    path: "/ui/media",
    name: "media",
    meta: { title: "Media", authRequired: true },
    component: () => import("../views/ui/media"),
  },
  {
    path: "/widgets",
    name: "widgets",
    meta: { title: "Widgets", authRequired: true },
    component: () => import("../views/widgets/index"),
  },
  {
    path: "/icons/boxicons",
    name: "boxicons-icon",
    meta: { title: "Boxicons", authRequired: true },
    component: () => import("../views/icons/boxicons"),
  },
  {
    path: "/icons/materialdesign",
    name: "Material Design",
    meta: { title: "Material Design", authRequired: true },
    component: () => import("../views/icons/materialdesign"),
  },
  {
    path: "/icons/feather",
    name: "feather",
    meta: { title: "Feather", authRequired: true },
    component: () => import("../views/icons/feather"),
  },
  {
    path: "/icons/lineawesome",
    name: "lineawesome",
    meta: { title: "Line Awesome", authRequired: true },
    component: () => import("../views/icons/lineawesome"),
  },
  {
    path: "/icons/remix",
    name: "remix",
    meta: { title: "Remix", authRequired: true },
    component: () => import("../views/icons/remix"),
  },
  {
    path: "/icons/crypto",
    name: "crypto",
    meta: { title: "Crypto", authRequired: true },
    component: () => import("../views/icons/crypto"),
  },
  {
    path: "/tables/basic",
    name: "basic-table",
    meta: { title: "Basic Table", authRequired: true },
    component: () => import("../views/tables/basic"),
  },
  {
    path: "/tables/gridjs",
    name: "gridjs",
    meta: { title: "Grid JS", authRequired: true },
    component: () => import("../views/tables/grid/index"),
  },
  {
    path: "/form/advanced",
    name: "form-advanced",
    meta: { title: "Form Advance", authRequired: true },
    component: () => import("../views/forms/advanced"),
  },
  {
    path: "/form/elements",
    name: "form-elements",
    meta: { title: "Form Elements", authRequired: true },
    component: () => import("../views/forms/elements"),
  },
  {
    path: "/form/layouts",
    name: "Form Layouts",
    meta: { title: "Form Layouts", authRequired: true },
    component: () => import("../views/forms/layouts"),
  },
  {
    path: "/form/editors",
    name: "Form Editors",
    meta: { title: "Form Editors", authRequired: true },
    component: () => import("../views/forms/editors"),
  },
  {
    path: "/form/file-uploads",
    name: "File Uploads",
    meta: { title: "File Uploads", authRequired: true },
    component: () => import("../views/forms/file-uploads"),
  },
  {
    path: "/form/validation",
    name: "Form Validation",
    meta: { title: "Form Validation", authRequired: true },
    component: () => import("../views/forms/validation"),
  },
  {
    path: "/form/wizard",
    name: "Form Wizard",
    meta: { title: "Form Wizard", authRequired: true },
    component: () => import("../views/forms/wizard"),
  },
  {
    path: "/form/masks",
    name: "form-mask",
    meta: { title: "Form Mask", authRequired: true },
    component: () => import("../views/forms/masks"),
  },
  {
    path: "/form/pickers",
    name: "form-pickers",
    meta: { title: "Form Picker", authRequired: true },
    component: () => import("../views/forms/pickers"),
  },
  {
    path: "/form/range-sliders",
    name: "form-range-slider",
    meta: { title: "Form Range Slider", authRequired: true },
    component: () => import("../views/forms/range-sliders"),
  },
  {
    path: "/form/select",
    name: "form-select",
    meta: { title: "Form Select", authRequired: true },
    component: () => import("../views/forms/select"),
  },
  {
    path: "/form/checkboxs-radios",
    name: "form-checkboxs-radio",
    meta: { title: "Checkboxs & Radios", authRequired: true },
    component: () => import("../views/forms/checkboxs-radios"),
  },
  {
    path: "/landing",
    name: "landing",
    meta: { title: "Landing", authRequired: true },
    component: () => import("../views/landing/index"),
  },
  {
    path: "/nft-landing",
    name: "nft-landing",
    meta: { title: "NFT Landing", authRequired: true },
    component: () => import("../views/landing/nft-landing"),
  },
  {
    path: "/job-landing",
    name: "job-landing",
    meta: { title: "Sunhera Jobs", authRequired: false },
    component: () => import("../views/landing/job-landing"),
  },
  // Sunhera public site — placeholder routes (point at coming-soon until real pages land)
  { path: "/find-jobs", name: "sunhera-find-jobs", meta: { title: "Find Jobs", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/about", name: "sunhera-about", meta: { title: "About Us", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/about/inspirations", name: "sunhera-about-inspirations", meta: { title: "Our Inspirations", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/about/mentor", name: "sunhera-about-mentor", meta: { title: "Our Mentor", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/about/founder", name: "sunhera-about-founder", meta: { title: "Our Founder", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/about/photo-gallery", name: "sunhera-about-gallery", meta: { title: "Photo Gallery", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/about/why-us", name: "sunhera-about-why-us", meta: { title: "Why Us", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/our-experts", name: "sunhera-our-experts", meta: { title: "Our Experts", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/blog", name: "sunhera-blog", meta: { title: "Blog", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/faq-candidate", name: "sunhera-faq-candidate", meta: { title: "FAQ — Candidate", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/faq-employer", name: "sunhera-faq-employer", meta: { title: "FAQ — Employer", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/contact", name: "sunhera-contact", meta: { title: "Contact Us", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/refund-policy", name: "sunhera-refund-policy", meta: { title: "Refund Policy", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/terms", name: "sunhera-terms", meta: { title: "Terms & Conditions", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/privacy", name: "sunhera-privacy", meta: { title: "Privacy Policy", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/auth/seeker-login", name: "sunhera-seeker-login", meta: { title: "Seeker Login", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  { path: "/auth/employer-login", name: "sunhera-employer-login", meta: { title: "Employer Login", authRequired: false }, component: () => import("../views/pages/coming-soon") },
  {
    path: "/pages/starter",
    name: "starter",
    meta: { title: "Starter", authRequired: true },
    component: () => import("../views/pages/starter"),
  },
  {
    path: "/pages/profile",
    name: "profile",
    meta: { title: "Profile", authRequired: true },
    component: () => import("../views/pages/profile/simple"),
  },
  {
    path: "/pages/profile-setting",
    name: "profile-setting",
    meta: { title: "Setting", authRequired: true },
    component: () => import("../views/pages/profile/setting"),
  },
  {
    path: "/pages/maintenance",
    name: "maintenance",
    meta: { title: "Maintanance", authRequired: true },
    component: () => import("../views/pages/maintenance"),
  },
  {
    path: "/pages/coming-soon",
    name: "coming-soon",
    meta: { title: "Comming Soon", authRequired: true },
    component: () => import("../views/pages/coming-soon"),
  },
  {
    path: "/pages/timeline",
    name: "timeline",
    meta: { title: "Timeline", authRequired: true },
    component: () => import("../views/pages/timeline"),
  },
  {
    path: "/pages/faqs",
    name: "FAQs",
    meta: { title: "FAQs", authRequired: true },
    component: () => import("../views/pages/faqs"),
  },
  {
    path: "/pages/pricing",
    name: "pricing",
    meta: { title: "Pricing", authRequired: true },
    component: () => import("../views/pages/pricing"),
  },
  {
    path: "/pages/team",
    name: "team",
    meta: { title: "Team", authRequired: true },
    component: () => import("../views/pages/team"),
  },
  {
    path: "/pages/search-results",
    name: "search-results",
    meta: { title: "Search Results", authRequired: true },
    component: () => import("../views/pages/search-results"),
  },
  {
    path: "/pages/sitemap",
    name: "sitemap",
    meta: { title: "Sitemap", authRequired: true },
    component: () => import("../views/pages/sitemap"),
  },
  {
    path: "/pages/privacy-policy",
    name: "privacy-policy",
    meta: { title: "Privacy Policy", authRequired: true },
    component: () => import("../views/pages/privacy-policy"),
  },
  {
    path: "/pages/term-conditions",
    name: "term-conditions",
    meta: { title: "Term Conditions", authRequired: true },
    component: () => import("../views/pages/term-conditions"),
  },
  {
    path: "/blogs/list-view",
    name: "List View",
    meta: { title: "Blogs", authRequired: true },
    component: () => import("../views/pages/blogs/list-view"),
  },
  {
    path: "/blogs/grid-view",
    name: "grid-view",
    meta: { title: "Blogs", authRequired: true },
    component: () => import("../views/pages/blogs/grid-view"),
  },
  {
    path: "/blogs/overview",
    name: "overview",
    meta: { title: "Blogs", authRequired: true },
    component: () => import("../views/pages/blogs/overview"),
  },
  {
    path: "/charts/chartjs",
    name: "chartjs-chart",
    meta: { title: "Chartjs", authRequired: true },
    component: () => import("../views/charts/chartjs/index"),
  },
  {
    path: "/charts/echart",
    name: "echart",
    meta: { title: "Echarts", authRequired: true },
    component: () => import("../views/charts/echart/index"),
  },
  {
    path: '/charts/apex-line',
    name: 'line',
    meta: {
      title: "Line Charts", authRequired: true,
    },
    component: () => import('../views/charts/apex/line/index')
  },
  {
    path: '/charts/apex-area',
    name: 'area-chart',
    meta: {
      title: "Area Charts", authRequired: true,
    },
    component: () => import('../views/charts/apex/area/index')
  },
  {
    path: '/charts/apex-bar',
    name: 'bar-chart',
    meta: {
      title: "Bar Charts", authRequired: true,
    },
    component: () => import('../views/charts/apex/bar/index')
  },
  {
    path: '/charts/apex-column',
    name: 'column-chart',
    meta: {
      title: "Column Charts", authRequired: true,
    },
    component: () => import('../views/charts/apex/column/index')
  },

  {
    path: '/charts/apex-mixed',
    name: 'mixed-chart',
    meta: {
      title: "Mixed Charts", authRequired: true,
    },
    component: () => import('../views/charts/apex/mixed/index')
  },
  {
    path: '/charts/apex-range-area',
    name: 'range-area-chart',
    meta: {
      title: "Range Area Charts", authRequired: true,
    },
    component: () => import('../views/charts/apex/rangearea/index')
  },
  {
    path: '/charts/apex-funnel',
    name: 'funnel-chart',
    meta: {
      title: "Funnel Charts", authRequired: true,
    },
    component: () => import('../views/charts/apex/funnel/index')
  },
  {
    path: '/charts/apex-candlestick',
    name: 'candlestick',
    meta: {
      title: "Candlestick", authRequired: true,
    },
    component: () => import('../views/charts/apex/candlestick/index')
  },
  {
    path: '/charts/apex-boxplot',
    name: 'boxplot',
    meta: {
      title: "Boxplot", authRequired: true,
    },
    component: () => import('../views/charts/apex/boxplot/index')
  },
  {
    path: '/charts/apex-bubble',
    name: 'bubble',
    meta: {
      title: "Bubble Chart", authRequired: true,
    },
    component: () => import('../views/charts/apex/bubble/index')
  },
  {
    path: '/charts/apex-scatter',
    name: 'scatter',
    meta: {
      title: "Scatter", authRequired: true,
    },
    component: () => import('../views/charts/apex/scatter/index')
  },
  {
    path: '/charts/apex-heatmap',
    name: 'heatmap',
    meta: {
      title: "Heatmap", authRequired: true,
    },
    component: () => import('../views/charts/apex/heatmap/index')
  },
  {
    path: '/charts/apex-treemap',
    name: 'treemap',
    meta: {
      title: "Treemap", authRequired: true,
    },
    component: () => import('../views/charts/apex/treemap/index')
  },
  {
    path: '/charts/apex-pie',
    name: 'pie',
    meta: {
      title: "Apex Pie", authRequired: true,
    },
    component: () => import('../views/charts/apex/pie/index')
  },
  {
    path: '/charts/apex-radialbar',
    name: 'radialbar',
    meta: {
      title: "Apex Radialbar", authRequired: true,
    },
    component: () => import('../views/charts/apex/radiabar/index')
  },
  {
    path: '/charts/apex-radar',
    name: 'radar',
    meta: {
      title: "Apex Radar", authRequired: true,
    },
    component: () => import('../views/charts/apex/radar/index')
  },
  {
    path: '/charts/apex-polararea',
    name: 'polararea',
    meta: {
      title: "Polararea", authRequired: true,
    },
    component: () => import('../views/charts/apex/polararea/index')
  },
  {
    path: '/charts/apex-slope',
    name: 'slope',
    meta: {
      title: "Slope", authRequired: true,
    },
    component: () => import('../views/charts/apex/slope/index')
  },
  {
    path: '/advance-ui/animation',
    name: 'animation',
    meta: {
      title: "Animation", authRequired: true,
    },
    component: () => import('../views/advance-ui/animation')
  },
  {
    path: '/advance-ui/highlight',
    name: 'highlight',
    meta: {
      title: "Highlight", authRequired: true,
    },
    component: () => import('../views/advance-ui/highlight')
  },
  {
    path: '/advance-ui/scrollbar',
    name: 'scrollbar',
    meta: {
      title: "Scrollbar", authRequired: true,
    },
    component: () => import('../views/advance-ui/scrollbar')
  },
  {
    path: '/advance-ui/scrollspy',
    name: 'scrollspy',
    meta: {
      title: "Scrollspy", authRequired: true,
    },
    component: () => import('../views/advance-ui/scrollspy')
  },
  {
    path: '/advance-ui/sweetalerts',
    name: 'sweetalerts',
    meta: {
      title: "Sweet Alerts", authRequired: true,
    },
    component: () => import('../views/advance-ui/sweetalerts')
  },
  {
    path: '/advance-ui/swiper',
    name: 'Swiper Slider',
    meta: {
      title: "Swiper", authRequired: true,
    },
    component: () => import('../views/advance-ui/swiper')
  },
  {
    path: '/auth/signin-basic',
    name: 'signin-basic',
    meta: {
      title: "Signin Basic", authRequired: true,
    },
    component: () => import('../views/auth/signin/basic')
  },
  {
    path: '/auth/signin-cover',
    name: 'signin-cover',
    meta: {
      title: "Signin Cover", authRequired: true,
    },
    component: () => import('../views/auth/signin/cover')
  },
  {
    path: '/auth/signup-basic',
    name: 'signup-basic',
    meta: {
      title: "Signup Basic", authRequired: true,
    },
    component: () => import('../views/auth/signup/basic')
  },
  {
    path: '/auth/signup-cover',
    name: 'signup-cover',
    meta: {
      title: "Signup Cover", authRequired: true,
    },
    component: () => import('../views/auth/signup/cover')
  },
  {
    path: '/auth/reset-pwd-basic',
    name: 'reset-pwd-basic',
    meta: {
      title: "Reset Password", authRequired: true,
    },
    component: () => import('../views/auth/reset/basic')
  },
  {
    path: '/auth/reset-pwd-cover',
    name: 'reset-pwd-cover',
    meta: {
      title: "Reset Password", authRequired: true,
    },
    component: () => import('../views/auth/reset/cover')
  },
  {
    path: '/auth/create-pwd-basic',
    name: 'create-pwd-basic',
    meta: {
      title: "create Password", authRequired: true,
    },
    component: () => import('../views/auth/create/basic')
  },
  {
    path: '/auth/create-pwd-cover',
    name: 'create-pwd-cover',
    meta: {
      title: "create Password", authRequired: true,
    },
    component: () => import('../views/auth/create/cover')
  },
  {
    path: '/auth/lockscreen-basic',
    name: 'lock-screen-basic',
    meta: {
      title: "Lock Screen", authRequired: true,
    },
    component: () => import('../views/auth/lockscreen/basic')
  },
  {
    path: '/auth/lockscreen-cover',
    name: 'lock-screen-cover',
    meta: {
      title: "Lock Screen", authRequired: true,
    },
    component: () => import('../views/auth/lockscreen/cover')
  },
  {
    path: '/auth/twostep-basic',
    name: 'twostep-basic',
    meta: {
      title: "Two Step Auth", authRequired: true,
    },
    component: () => import('../views/auth/twostep/basic')
  }, {
    path: '/auth/twostep-cover',
    name: 'twostep-cover',
    meta: {
      title: "Two Step Auth", authRequired: true,
    },
    component: () => import('../views/auth/twostep/cover')
  },
  {
    path: '/auth/404',
    name: '404',
    meta: {
      title: "Error 404", authRequired: true,
    },
    component: () => import('../views/auth/errors/404')
  },
  {
    path: '/auth/500',
    name: '500',
    meta: {
      title: "Error 500", authRequired: true,
    },
    component: () => import('../views/auth/errors/500')
  },
  {
    path: '/auth/404-basic',
    name: '404-basic',
    meta: {
      title: "Error 404", authRequired: true,
    },
    component: () => import('../views/auth/errors/404-basic')
  },
  {
    path: '/auth/404-cover',
    name: '404-cover',
    meta: {
      title: "Error 404", authRequired: true,
    },
    component: () => import('../views/auth/errors/404-cover')
  },
  {
    path: '/auth/ofline',
    name: 'ofline',
    meta: {
      title: "Offline", authRequired: true,
    },
    component: () => import('../views/auth/errors/ofline')
  },
  {
    path: '/auth/logout-basic',
    name: 'logout-basic',
    meta: {
      title: "Logout", authRequired: true,
    },
    component: () => import('../views/auth/logout/basic')
  },
  {
    path: '/auth/logout-cover',
    name: 'logout-cover',
    meta: {
      title: "Logout", authRequired: true,
    },
    component: () => import('../views/auth/logout/cover')
  },
  {
    path: '/auth/success-msg-basic',
    name: 'success-msg-basic',
    meta: {
      title: "Success Message", authRequired: true,
    },
    component: () => import('../views/auth/success-msg/basic')
  },
  {
    path: '/auth/success-msg-cover',
    name: 'success-msg-cover',
    meta: {
      title: "Success Message", authRequired: true,
    },
    component: () => import('../views/auth/success-msg/cover')
  },
  {
    path: "/maps/google",
    name: "Google Maps",
    meta: {
      title: "Google Maps", authRequired: true,
    },
    component: () => import("../views/maps/google"),
  },
  {
    path: "/maps/leaflet",
    name: "Leaflet Maps",
    meta: { title: "Leaflet Maps", authRequired: true },
    component: () => import("../views/maps/leaflet/index"),
  },

  // ─── ERP Routes ───
  {
    path: "/erp/companies",
    name: "ERP Companies",
    meta: { title: "Companies", authRequired: true },
    component: () => import("../views/erp/companies/List"),
  },
  {
    path: "/erp/account-groups",
    name: "ERP Account Groups",
    meta: { title: "Account Groups", authRequired: true },
    component: () => import("../views/erp/account-groups/List"),
  },
  {
    path: "/erp/ledgers",
    name: "ERP Ledgers",
    meta: { title: "Ledgers", authRequired: true },
    component: () => import("../views/erp/ledgers/List"),
  },
  {
    path: "/erp/ledgers/create",
    name: "ERP Ledger Create",
    meta: { title: "Create Ledger", authRequired: true },
    component: () => import("../views/erp/ledgers/Form"),
  },
  {
    path: "/erp/ledgers/:id/vouchers",
    name: "ERP Ledger Vouchers",
    meta: { title: "Ledger Vouchers", authRequired: true },
    component: () => import("../views/erp/ledgers/Vouchers"),
  },
  {
    path: "/erp/ledgers/:id",
    name: "ERP Ledger Edit",
    meta: { title: "Edit Ledger", authRequired: true },
    component: () => import("../views/erp/ledgers/Form"),
  },
  {
    path: "/erp/shared-ledgers",
    name: "ERP Shared Ledgers",
    meta: { title: "Shared Ledgers", authRequired: true },
    component: () => import("../views/erp/shared-ledgers/List"),
  },
  {
    path: "/erp/shared-ledgers/create",
    name: "ERP Shared Ledger Create",
    meta: { title: "Create Shared Ledger", authRequired: true },
    component: () => import("../views/erp/shared-ledgers/Form"),
  },
  {
    path: "/erp/shared-ledgers/:id",
    name: "ERP Shared Ledger Edit",
    meta: { title: "Edit Shared Ledger", authRequired: true },
    component: () => import("../views/erp/shared-ledgers/Form"),
  },
  {
    path: "/erp/shared-ledgers/:id/balance",
    name: "ERP Shared Ledger Balance",
    meta: { title: "Consolidated Balance", authRequired: true },
    component: () => import("../views/erp/shared-ledgers/ConsolidatedBalance"),
  },

  // ─── Districts & Cities ───
  {
    path: "/erp/districts-cities",
    name: "ERP Districts & Cities",
    meta: { title: "Districts & Cities", authRequired: true },
    component: () => import("../views/erp/districts-cities/List"),
  },

  // ─── Shared Inventory Masters ───
  {
    path: "/erp/shared-uom",
    name: "ERP Shared UOM",
    meta: { title: "Shared UOM", authRequired: true },
    component: () => import("../views/erp/shared-uom/List"),
  },
  {
    path: "/erp/shared-uom/create",
    name: "ERP Shared UOM Create",
    meta: { title: "Create Shared UOM", authRequired: true },
    component: () => import("../views/erp/shared-uom/Form"),
  },
  {
    path: "/erp/shared-uom/:id",
    name: "ERP Shared UOM Edit",
    meta: { title: "Edit Shared UOM", authRequired: true },
    component: () => import("../views/erp/shared-uom/Form"),
  },
  {
    path: "/erp/shared-stock-item-lookups",
    name: "ERP Shared Stock Item Lookups",
    meta: { title: "Shared Lookups", authRequired: true },
    component: () => import("../views/erp/shared-stock-item-lookups/List"),
  },
  {
    path: "/erp/shared-stock-item-lookups/create",
    name: "ERP Shared Stock Item Lookup Create",
    meta: { title: "Create Shared Lookup", authRequired: true },
    component: () => import("../views/erp/shared-stock-item-lookups/Form"),
  },
  {
    path: "/erp/shared-stock-item-lookups/:id",
    name: "ERP Shared Stock Item Lookup Edit",
    meta: { title: "Edit Shared Lookup", authRequired: true },
    component: () => import("../views/erp/shared-stock-item-lookups/Form"),
  },
  {
    path: "/erp/shared-stock-groups",
    name: "ERP Shared Stock Groups",
    meta: { title: "Shared Stock Groups", authRequired: true },
    component: () => import("../views/erp/shared-stock-groups/List"),
  },
  {
    path: "/erp/shared-stock-groups/create",
    name: "ERP Shared Stock Group Create",
    meta: { title: "Create Shared Stock Group", authRequired: true },
    component: () => import("../views/erp/shared-stock-groups/Form"),
  },
  {
    path: "/erp/shared-stock-groups/:id",
    name: "ERP Shared Stock Group Edit",
    meta: { title: "Edit Shared Stock Group", authRequired: true },
    component: () => import("../views/erp/shared-stock-groups/Form"),
  },
  {
    path: "/erp/shared-categories",
    name: "ERP Shared Categories",
    meta: { title: "Shared Categories", authRequired: true },
    component: () => import("../views/erp/shared-categories/List"),
  },
  {
    path: "/erp/shared-categories/create",
    name: "ERP Shared Category Create",
    meta: { title: "Create Shared Category", authRequired: true },
    component: () => import("../views/erp/shared-categories/Form"),
  },
  {
    path: "/erp/shared-categories/:id",
    name: "ERP Shared Category Edit",
    meta: { title: "Edit Shared Category", authRequired: true },
    component: () => import("../views/erp/shared-categories/Form"),
  },
  {
    path: "/erp/shared-godowns",
    name: "ERP Shared Godowns",
    meta: { title: "Shared Godowns", authRequired: true },
    component: () => import("../views/erp/shared-godowns/List"),
  },
  {
    path: "/erp/shared-godowns/create",
    name: "ERP Shared Godown Create",
    meta: { title: "Create Shared Godown", authRequired: true },
    component: () => import("../views/erp/shared-godowns/Form"),
  },
  {
    path: "/erp/shared-godowns/:id",
    name: "ERP Shared Godown Edit",
    meta: { title: "Edit Shared Godown", authRequired: true },
    component: () => import("../views/erp/shared-godowns/Form"),
  },
  {
    path: "/erp/shared-stock-items",
    name: "ERP Shared Stock Items",
    meta: { title: "Shared Stock Items", authRequired: true },
    component: () => import("../views/erp/shared-stock-items/List"),
  },
  {
    path: "/erp/shared-stock-items/create",
    name: "ERP Shared Stock Item Create",
    meta: { title: "Create Shared Stock Item", authRequired: true },
    component: () => import("../views/erp/shared-stock-items/Form"),
  },
  {
    path: "/erp/shared-stock-items/:id",
    name: "ERP Shared Stock Item Edit",
    meta: { title: "Edit Shared Stock Item", authRequired: true },
    component: () => import("../views/erp/shared-stock-items/Form"),
  },
  {
    path: "/erp/shared-stock-items/:id/consolidated-stock",
    name: "ERP Shared Stock Item Consolidated",
    meta: { title: "Consolidated Stock", authRequired: true },
    component: () => import("../views/erp/shared-stock-items/ConsolidatedStock"),
  },

  // ─── Phase 3: Configuration ───
  {
    path: "/erp/financial-years",
    name: "ERP Financial Years",
    meta: { title: "Financial Years", authRequired: true },
    component: () => import("../views/erp/financial-years/List"),
  },
  {
    path: "/erp/cost-categories",
    name: "ERP Cost Categories",
    meta: { title: "Cost Categories", authRequired: true },
    component: () => import("../views/erp/cost-categories/List"),
  },
  {
    path: "/erp/cost-centers",
    name: "ERP Cost Centers",
    meta: { title: "Cost Centers", authRequired: true },
    component: () => import("../views/erp/cost-centers/List"),
  },
  {
    path: "/erp/cost-center-summary",
    name: "ERP Cost Center Summary",
    meta: { title: "Cost Category Summary", authRequired: true },
    component: () => import("../views/erp/cost-centers/CategorySummary"),
  },
  {
    path: "/erp/cost-centers/:id/vouchers",
    name: "ERP Cost Center Vouchers",
    meta: { title: "Cost Centre Vouchers", authRequired: true },
    component: () => import("../views/erp/cost-centers/Vouchers"),
  },
  {
    path: "/erp/cost-centers/:id/breakup",
    name: "ERP Cost Center Breakup",
    meta: { title: "Cost Centre Break-up", authRequired: true },
    component: () => import("../views/erp/cost-centers/Breakup"),
  },
  {
    path: "/erp/uom",
    name: "ERP Units of Measurement",
    meta: { title: "Units of Measurement", authRequired: true },
    component: () => import("../views/erp/uom/List"),
  },

  // ─── Phase 3: Inventory ───
  {
    path: "/erp/categories",
    name: "ERP Categories",
    meta: { title: "Item Categories", authRequired: true },
    component: () => import("../views/erp/categories/List"),
  },
  {
    path: "/erp/stock-item-lookups",
    name: "ERP Stock Item Lookups",
    meta: { title: "Stock Item Lookups", authRequired: true },
    component: () => import("../views/erp/stock-item-lookups/List"),
  },
  {
    path: "/erp/stock-groups",
    name: "ERP Stock Groups",
    meta: { title: "Stock Groups", authRequired: true },
    component: () => import("../views/erp/stock-groups/List"),
  },
  {
    path: "/erp/godowns",
    name: "ERP Godowns",
    meta: { title: "Godowns / Locations", authRequired: true },
    component: () => import("../views/erp/godowns/List"),
  },
  {
    path: "/erp/hsn-sac",
    name: "ERP HSN/SAC",
    meta: { title: "HSN/SAC Codes", authRequired: true },
    component: () => import("../views/erp/hsn-sac/List"),
  },
  {
    path: "/erp/stock-items",
    name: "ERP Stock Items",
    meta: { title: "Stock Items", authRequired: true },
    component: () => import("../views/erp/stock-items/List"),
  },
  {
    path: "/erp/stock-items/create",
    name: "ERP Stock Item Create",
    meta: { title: "Create Stock Item", authRequired: true },
    component: () => import("../views/erp/stock-items/Form"),
  },
  {
    path: "/erp/stock-items/:id",
    name: "ERP Stock Item Edit",
    meta: { title: "Edit Stock Item", authRequired: true },
    component: () => import("../views/erp/stock-items/Form"),
  },

  // ─── Phase 3: Tax ───
  {
    path: "/erp/tax-rates",
    name: "ERP Tax Rates",
    meta: { title: "Tax Rates (GST)", authRequired: true },
    component: () => import("../views/erp/tax-rates/List"),
  },
  {
    path: "/erp/tds-tcs",
    name: "ERP TDS/TCS",
    meta: { title: "TDS/TCS Sections", authRequired: true },
    component: () => import("../views/erp/tds-tcs/List"),
  },
  {
    path: "/erp/interest-masters",
    name: "ERP Interest Master",
    meta: { title: "Interest Master", authRequired: true },
    component: () => import("../views/erp/interest-masters/List"),
  },

  // ─── Tax Returns & Compliance ───
  {
    path: "/erp/tax-returns/gstr1",
    name: "ERP GSTR-1",
    meta: { title: "GSTR-1 (Outward Supplies)", authRequired: true },
    component: () => import("../views/erp/tax-returns/Gstr1"),
  },
  {
    path: "/erp/tax-returns/gstr3b",
    name: "ERP GSTR-3B",
    meta: { title: "GSTR-3B (Summary Return)", authRequired: true },
    component: () => import("../views/erp/tax-returns/Gstr3b"),
  },
  {
    path: "/erp/tax-returns/gstr2b",
    name: "ERP GSTR-2B",
    meta: { title: "GSTR-2B (Purchase Register)", authRequired: true },
    component: () => import("../views/erp/tax-returns/Gstr2b"),
  },
  {
    path: "/erp/tax-returns/gstr9",
    name: "ERP GSTR-9",
    meta: { title: "GSTR-9 (Annual Return)", authRequired: true },
    component: () => import("../views/erp/tax-returns/Gstr9"),
  },
  {
    path: "/erp/tax-returns/itc-ledger",
    name: "ERP ITC Ledger",
    meta: { title: "ITC Ledger", authRequired: true },
    component: () => import("../views/erp/tax-returns/ItcLedger"),
  },
  {
    path: "/erp/tax-returns/itc-reconcile",
    name: "ERP ITC Reconciliation",
    meta: { title: "ITC Reconciliation", authRequired: true },
    component: () => import("../views/erp/tax-returns/ItcReconciliation"),
  },
  {
    path: "/erp/tax-returns/filings",
    name: "ERP Filing Tracker",
    meta: { title: "Filing Tracker", authRequired: true },
    component: () => import("../views/erp/tax-returns/FilingList"),
  },
  {
    path: "/erp/tax-returns/challans",
    name: "ERP Tax Challans",
    meta: { title: "Tax Challans", authRequired: true },
    component: () => import("../views/erp/tax-returns/ChallanList"),
  },
  {
    path: "/erp/tax-returns/challans/new",
    name: "ERP Tax Challan Create",
    meta: { title: "New Tax Challan", authRequired: true },
    component: () => import("../views/erp/tax-returns/ChallanForm"),
  },
  {
    path: "/erp/tax-returns/challans/:id/edit",
    name: "ERP Tax Challan Edit",
    meta: { title: "Edit Tax Challan", authRequired: true },
    component: () => import("../views/erp/tax-returns/ChallanForm"),
  },
  {
    // Phase 5w4 — redirect to the unified print route. Legacy bookmarks
    // keep working; the unified host renders the same statutory format.
    path: "/erp/tax-returns/challans/:id/print",
    name: "ERP Tax Challan Print",
    redirect: (to) => `/print/TaxChallan/${to.params.id}?autoprint=1`,
  },
  {
    path: "/erp/tax-returns/tds/form26q",
    name: "ERP TDS Form 26Q",
    meta: { title: "TDS Return (Form 26Q)", authRequired: true },
    component: () => import("../views/erp/tax-returns/Form26Q"),
  },
  {
    path: "/erp/tax-returns/tds/form27q",
    name: "ERP TDS Form 27Q",
    meta: { title: "TDS Return — Non-Resident (Form 27Q)", authRequired: true },
    component: () => import("../views/erp/tax-returns/Form27Q"),
  },
  {
    path: "/erp/tax-returns/tds/form16a",
    name: "ERP TDS Form 16A",
    meta: { title: "TDS Certificate (Form 16A)", authRequired: true },
    component: () => import("../views/erp/tax-returns/Form16A"),
  },
  {
    path: "/erp/tax-returns/tcs/form27eq",
    name: "ERP TCS Form 27EQ",
    meta: { title: "TCS Return (Form 27EQ)", authRequired: true },
    component: () => import("../views/erp/tax-returns/Form27EQ"),
  },
  {
    path: "/erp/tax-returns/eway-bills",
    name: "ERP E-Way Bills",
    meta: { title: "E-Way Bills", authRequired: true },
    component: () => import("../views/erp/tax-returns/EWayBillList"),
  },
  {
    path: "/erp/tax-returns/eway-bills/new",
    name: "ERP E-Way Bill Create",
    meta: { title: "New E-Way Bill", authRequired: true },
    component: () => import("../views/erp/tax-returns/EWayBillForm"),
  },
  {
    path: "/erp/tax-returns/eway-bills/config",
    name: "ERP E-Way Bill Config",
    meta: { title: "E-Way Bill Configuration", authRequired: true },
    component: () => import("../views/erp/tax-returns/EWayBillConfig"),
  },
  {
    path: "/erp/tax-returns/eway-bills/exceptions",
    name: "ERP E-Way Bill Exceptions",
    meta: { title: "E-Way Bill Exceptions", authRequired: true },
    component: () => import("../views/erp/tax-returns/EWayBillList"),
    props: { initialStatus: "Exception" },
  },
  {
    path: "/erp/tax-returns/eway-bills/expiring-soon",
    name: "ERP E-Way Bill Expiring Soon",
    meta: { title: "E-Way Bills Expiring Soon", authRequired: true },
    component: () => import("../views/erp/tax-returns/EWayBillList"),
    props: { expiringOnly: true },
  },
  {
    path: "/erp/tax-returns/eway-bills/reconciliation",
    name: "ERP E-Way Bill Reconciliation",
    meta: { title: "E-Way Bill Reconciliation", authRequired: true },
    component: () => import("../views/erp/tax-returns/EWayBillReconciliation"),
  },
  {
    // Phase 5w4 — redirect to the unified print route.
    path: "/erp/tax-returns/eway-bills/:id/print",
    name: "ERP E-Way Bill Print",
    redirect: (to) => `/print/EWayBill/${to.params.id}?autoprint=1`,
  },
  {
    path: "/erp/tax-returns/consolidated-eway-bills",
    name: "ERP Consolidated E-Way Bills",
    meta: { title: "Consolidated E-Way Bills", authRequired: true },
    component: () => import("../views/erp/tax-returns/ConsolidatedEWayBillList"),
  },
  {
    path: "/erp/tax-returns/consolidated-eway-bills/new",
    name: "ERP Consolidated E-Way Bill Create",
    meta: { title: "New Consolidated E-Way Bill", authRequired: true },
    component: () => import("../views/erp/tax-returns/ConsolidatedEWayBillForm"),
  },
  {
    path: "/erp/tax-returns/consolidated-eway-bills/:id",
    name: "ERP Consolidated E-Way Bill Edit",
    meta: { title: "Edit Consolidated E-Way Bill", authRequired: true },
    component: () => import("../views/erp/tax-returns/ConsolidatedEWayBillForm"),
  },
  {
    path: "/erp/tax-returns/eway-bills/:id",
    name: "ERP E-Way Bill Edit",
    meta: { title: "Edit E-Way Bill", authRequired: true },
    component: () => import("../views/erp/tax-returns/EWayBillForm"),
  },
  {
    path: "/erp/tax-returns/calendar",
    name: "ERP Tax Calendar",
    meta: { title: "Tax Compliance Calendar", authRequired: true },
    component: () => import("../views/erp/tax-returns/TaxCalendar"),
  },
  {
    path: "/erp/tax-returns/dashboard",
    name: "ERP Tax Dashboard",
    meta: { title: "Tax Dashboard", authRequired: true },
    component: () => import("../views/erp/tax-returns/TaxDashboard"),
  },

  // ─── Document Numbering ───
  {
    path: "/erp/document-numbering",
    name: "ERP Document Numbering",
    meta: { title: "Document Numbering", authRequired: true },
    component: () => import("../views/erp/document-numbering/List"),
  },

  // ─── Phase 4: Pricing (company-independent) ───
  {
    path: "/erp/shared-price-lists",
    name: "ERP Shared Price Lists",
    meta: { title: "Price Lists", authRequired: true },
    component: () => import("../views/erp/price-lists/List"),
  },
  {
    path: "/erp/shared-price-formulas",
    name: "ERP Shared Price Formulas",
    meta: { title: "Price Formulas", authRequired: true },
    component: () => import("../views/erp/price-formulas/List"),
  },
  {
    path: "/erp/base-price-report",
    name: "ERP Base Price Report",
    meta: { title: "Base Price Report", authRequired: true },
    component: () => import("../views/erp/base-prices/Report"),
  },
  {
    path: "/erp/program-tolerances",
    name: "ERP Program Tolerance",
    meta: { title: "Program Tolerance", authRequired: true },
    component: () => import("../views/erp/program-tolerances/List"),
  },

  // ─── Quotations ───
  {
    path: "/erp/quotations",
    name: "ERP Quotations",
    meta: { title: "Quotations", authRequired: true },
    component: () => import("../views/erp/quotations/List"),
  },
  {
    path: "/erp/quotations/create",
    name: "ERP Quotation Create",
    meta: { title: "Create Quotation", authRequired: true },
    component: () => import("../views/erp/quotations/Form"),
  },
  {
    path: "/erp/quotations/:id/details",
    name: "ERP Quotation Details",
    meta: { title: "Quotation Details", authRequired: true },
    component: () => import("../views/erp/quotations/Details"),
  },
  {
    path: "/erp/quotations/:id",
    name: "ERP Quotation Edit",
    meta: { title: "Edit Quotation", authRequired: true },
    component: () => import("../views/erp/quotations/Form"),
  },

  // ─── Sale Orders ───
  {
    path: "/erp/sales-orders",
    name: "ERP Sale Orders",
    meta: { title: "Sale Orders", authRequired: true },
    component: () => import("../views/erp/sales-orders/List"),
  },
  {
    path: "/erp/sales-orders/create",
    name: "ERP Sale Order Create",
    meta: { title: "Create Sale Order", authRequired: true },
    component: () => import("../views/erp/sales-orders/Form"),
  },
  {
    path: "/erp/sales-orders/:id/details",
    name: "ERP Sale Order Details",
    meta: { title: "Order Details", authRequired: true },
    component: () => import("../views/erp/sales-orders/Details"),
  },
  {
    path: "/erp/sales-orders/:id",
    name: "ERP Sale Order Edit",
    meta: { title: "Edit Sale Order", authRequired: true },
    component: () => import("../views/erp/sales-orders/Form"),
  },

  // ─── Sale Programs ───
  {
    path: "/erp/sale-programs",
    name: "ERP Sale Programs",
    meta: { title: "Sale Programs", authRequired: true },
    component: () => import("../views/erp/sale-programs/List"),
  },
  {
    path: "/erp/sale-programs/create",
    name: "ERP Sale Program Create",
    meta: { title: "Create Sale Program", authRequired: true },
    component: () => import("../views/erp/sale-programs/Form"),
  },
  {
    path: "/erp/sale-programs/:id/details",
    name: "ERP Sale Program Details",
    meta: { title: "Program Details", authRequired: true },
    component: () => import("../views/erp/sale-programs/Details"),
  },
  {
    // Phase 5w4 — redirect to the unified print route.
    path: "/erp/sale-programs/:id/print-advice",
    name: "ERP Sale Program Print Advice",
    redirect: (to) => `/print/PrintAdvice/${to.params.id}?autoprint=1`,
  },
  {
    path: "/erp/sale-programs/:id",
    name: "ERP Sale Program Edit",
    meta: { title: "Edit Sale Program", authRequired: true },
    component: () => import("../views/erp/sale-programs/Form"),
  },

  // ─── Sale Return Programs ───
  {
    path: "/erp/sale-return-programs",
    name: "ERP Sale Return Programs",
    meta: { title: "Sale Return Programs", authRequired: true },
    component: () => import("../views/erp/sale-return-programs/List"),
  },
  {
    path: "/erp/sale-return-programs/create",
    name: "ERP Sale Return Program Create",
    meta: { title: "Create Sale Return Program", authRequired: true },
    component: () => import("../views/erp/sale-return-programs/Form"),
  },
  {
    path: "/erp/sale-return-programs/:id/details",
    name: "ERP Sale Return Program Details",
    meta: { title: "Sale Return Program Details", authRequired: true },
    component: () => import("../views/erp/sale-return-programs/Details"),
  },
  {
    path: "/erp/sale-return-programs/:id",
    name: "ERP Sale Return Program Edit",
    meta: { title: "Edit Sale Return Program", authRequired: true },
    component: () => import("../views/erp/sale-return-programs/Form"),
  },

  // ─── Purchase Orders ───
  {
    path: "/erp/purchase-orders",
    name: "ERP Purchase Orders",
    meta: { title: "Purchase Orders", authRequired: true },
    component: () => import("../views/erp/purchase-orders/List"),
  },
  {
    path: "/erp/purchase-orders/create",
    name: "ERP Purchase Order Create",
    meta: { title: "Create Purchase Order", authRequired: true },
    component: () => import("../views/erp/purchase-orders/Form"),
  },
  {
    path: "/erp/purchase-orders/:id/details",
    name: "ERP Purchase Order Details",
    meta: { title: "Purchase Order Details", authRequired: true },
    component: () => import("../views/erp/purchase-orders/Details"),
  },
  {
    path: "/erp/purchase-orders/:id",
    name: "ERP Purchase Order Edit",
    meta: { title: "Edit Purchase Order", authRequired: true },
    component: () => import("../views/erp/purchase-orders/Form"),
  },

  // ─── Service Invoices ───
  {
    path: "/erp/service-invoices",
    name: "ERP Service Invoices",
    meta: { title: "Service Invoices", authRequired: true },
    component: () => import("../views/erp/service-invoices/List"),
  },
  {
    path: "/erp/service-invoices/new",
    name: "ERP Service Invoice Create",
    meta: { title: "New Service Invoice", authRequired: true },
    component: () => import("../views/erp/service-invoices/Form"),
  },
  {
    path: "/erp/service-invoices/:id/edit",
    name: "ERP Service Invoice Edit",
    meta: { title: "Edit Service Invoice", authRequired: true },
    component: () => import("../views/erp/service-invoices/Form"),
  },

  // ─── Service Bills ───
  {
    path: "/erp/service-bills",
    name: "ERP Service Bills",
    meta: { title: "Service Bills", authRequired: true },
    component: () => import("../views/erp/service-bills/List"),
  },
  {
    path: "/erp/service-bills/new",
    name: "ERP Service Bill Create",
    meta: { title: "New Service Bill", authRequired: true },
    component: () => import("../views/erp/service-bills/Form"),
  },
  {
    path: "/erp/service-bills/:id/edit",
    name: "ERP Service Bill Edit",
    meta: { title: "Edit Service Bill", authRequired: true },
    component: () => import("../views/erp/service-bills/Form"),
  },

  // ─── Service Sale Orders (pages in Task 12) ───
  {
    path: "/erp/service-sale-orders",
    name: "ERP Service Sale Orders",
    meta: { title: "Service Sale Orders", authRequired: true },
    component: () => import("../views/erp/service-sale-orders/List"),
  },
  {
    path: "/erp/service-sale-orders/new",
    name: "ERP Service Sale Order Create",
    meta: { title: "New Service Sale Order", authRequired: true },
    component: () => import("../views/erp/service-sale-orders/Form"),
  },
  {
    path: "/erp/service-sale-orders/:id/edit",
    name: "ERP Service Sale Order Edit",
    meta: { title: "Edit Service Sale Order", authRequired: true },
    component: () => import("../views/erp/service-sale-orders/Form"),
  },

  // ─── Service Purchase Orders (pages in Task 12) ───
  {
    path: "/erp/service-purchase-orders",
    name: "ERP Service Purchase Orders",
    meta: { title: "Service Purchase Orders", authRequired: true },
    component: () => import("../views/erp/service-purchase-orders/List"),
  },
  {
    path: "/erp/service-purchase-orders/new",
    name: "ERP Service Purchase Order Create",
    meta: { title: "New Service Purchase Order", authRequired: true },
    component: () => import("../views/erp/service-purchase-orders/Form"),
  },
  {
    path: "/erp/service-purchase-orders/:id/edit",
    name: "ERP Service Purchase Order Edit",
    meta: { title: "Edit Service Purchase Order", authRequired: true },
    component: () => import("../views/erp/service-purchase-orders/Form"),
  },

  // ─── Purchase Programs ───
  {
    path: "/erp/purchase-programs",
    name: "ERP Purchase Programs",
    meta: { title: "Purchase Programs", authRequired: true },
    component: () => import("../views/erp/purchase-programs/List"),
  },
  {
    path: "/erp/purchase-programs/create",
    name: "ERP Purchase Program Create",
    meta: { title: "Create Purchase Program", authRequired: true },
    component: () => import("../views/erp/purchase-programs/Form"),
  },
  {
    path: "/erp/purchase-programs/:id/details",
    name: "ERP Purchase Program Details",
    meta: { title: "Purchase Program Details", authRequired: true },
    component: () => import("../views/erp/purchase-programs/Details"),
  },
  {
    path: "/erp/purchase-programs/:id",
    name: "ERP Purchase Program Edit",
    meta: { title: "Edit Purchase Program", authRequired: true },
    component: () => import("../views/erp/purchase-programs/Form"),
  },
  {
    path: "/erp/traceability/raw-units",
    name: "ERP Raw Material Traceability",
    meta: { title: "Raw Material Traceability", authRequired: true },
    component: () => import("../views/erp/traceability/RawUnits"),
  },
  {
    path: "/erp/traceability/inquiry",
    name: "ERP Trace Inquiry",
    meta: { title: "Trace Inquiry", authRequired: true },
    component: () => import("../views/erp/traceability/Inquiry"),
  },

  // ─── Manufacturing Programs ───
  {
    path: "/erp/manufacturing-programs",
    name: "ERP Manufacturing Programs",
    meta: { title: "Manufacturing Programs", authRequired: true },
    component: () => import("../views/erp/manufacturing-programs/List"),
  },
  {
    path: "/erp/manufacturing-programs/create",
    name: "manufacturing-program-create",
    meta: { title: "Create Manufacturing Program", authRequired: true },
    component: () => import("../views/erp/manufacturing-programs/Form"),
  },
  {
    path: "/erp/manufacturing-programs/:id/details",
    name: "manufacturing-program-details",
    meta: { title: "Manufacturing Program Details", authRequired: true },
    component: () => import("../views/erp/manufacturing-programs/Details"),
  },
  {
    path: "/erp/manufacturing-programs/:id",
    name: "manufacturing-program-edit",
    meta: { title: "Edit Manufacturing Program", authRequired: true },
    component: () => import("../views/erp/manufacturing-programs/Form"),
  },

  // ─── Purchase Return Programs ───
  {
    path: "/erp/purchase-return-programs",
    name: "ERP Purchase Return Programs",
    meta: { title: "Purchase Return Programs", authRequired: true },
    component: () => import("../views/erp/purchase-return-programs/List"),
  },
  {
    path: "/erp/purchase-return-programs/create",
    name: "ERP Purchase Return Program Create",
    meta: { title: "Create Purchase Return Program", authRequired: true },
    component: () => import("../views/erp/purchase-return-programs/Form"),
  },
  {
    path: "/erp/purchase-return-programs/:id/details",
    name: "ERP Purchase Return Program Details",
    meta: { title: "Purchase Return Program Details", authRequired: true },
    component: () => import("../views/erp/purchase-return-programs/Details"),
  },
  {
    path: "/erp/purchase-return-programs/:id",
    name: "ERP Purchase Return Program Edit",
    meta: { title: "Edit Purchase Return Program", authRequired: true },
    component: () => import("../views/erp/purchase-return-programs/Form"),
  },

  // ─── Purchase MOUs ───
  {
    path: "/erp/purchase-mous",
    name: "ERP Purchase MOUs",
    meta: { title: "Purchase MOUs", authRequired: true },
    component: () => import("../views/erp/purchase-mous/List"),
  },
  {
    path: "/erp/purchase-mous/create",
    name: "ERP Purchase MOU Create",
    meta: { title: "Create Purchase MOU", authRequired: true },
    component: () => import("../views/erp/purchase-mous/Form"),
  },
  {
    path: "/erp/purchase-mous/:id/details",
    name: "ERP Purchase MOU Details",
    meta: { title: "Purchase MOU Details", authRequired: true },
    component: () => import("../views/erp/purchase-mous/Details"),
  },
  {
    path: "/erp/purchase-mous/:id",
    name: "ERP Purchase MOU Edit",
    meta: { title: "Edit Purchase MOU", authRequired: true },
    component: () => import("../views/erp/purchase-mous/Form"),
  },

  // ─── Purchase Rate Agreements ───
  {
    path: "/erp/purchase-rate-agreements",
    name: "ERP Purchase Rate Agreements",
    meta: { title: "Purchase Rate Agreements", authRequired: true },
    component: () => import("../views/erp/purchase-rate-agreements/List"),
  },
  {
    path: "/erp/purchase-rate-agreements/create",
    name: "ERP Purchase Rate Agreement Create",
    meta: { title: "Create Purchase Rate Agreement", authRequired: true },
    component: () => import("../views/erp/purchase-rate-agreements/Form"),
  },
  {
    path: "/erp/purchase-rate-agreements/:id/details",
    name: "ERP Purchase Rate Agreement Details",
    meta: { title: "Purchase Rate Agreement Details", authRequired: true },
    component: () => import("../views/erp/purchase-rate-agreements/Details"),
  },
  {
    path: "/erp/purchase-rate-agreements/:id",
    name: "ERP Purchase Rate Agreement Edit",
    meta: { title: "Edit Purchase Rate Agreement", authRequired: true },
    component: () => import("../views/erp/purchase-rate-agreements/Form"),
  },

  // ─── Sale Rate Agreements ───
  {
    path: "/erp/sale-rate-agreements",
    name: "ERP Sale Rate Agreements",
    meta: { title: "Sale Rate Agreements", authRequired: true },
    component: () => import("../views/erp/sale-rate-agreements/List"),
  },
  {
    path: "/erp/sale-rate-agreements/create",
    name: "ERP Sale Rate Agreement Create",
    meta: { title: "Create Sale Rate Agreement", authRequired: true },
    component: () => import("../views/erp/sale-rate-agreements/Form"),
  },
  {
    path: "/erp/sale-rate-agreements/:id/details",
    name: "ERP Sale Rate Agreement Details",
    meta: { title: "Sale Rate Agreement Details", authRequired: true },
    component: () => import("../views/erp/sale-rate-agreements/Details"),
  },
  {
    path: "/erp/sale-rate-agreements/:id",
    name: "ERP Sale Rate Agreement Edit",
    meta: { title: "Edit Sale Rate Agreement", authRequired: true },
    component: () => import("../views/erp/sale-rate-agreements/Form"),
  },

  // ─── Account Settings ───
  {
    path: "/erp/account-settings",
    name: "ERP Account Settings",
    meta: { title: "Account Settings", authRequired: true },
    component: () => import("../views/erp/account-settings/Form"),
  },

  // ─── Dev Utilities ───
  {
    path: "/erp/dev-utility",
    name: "ERP Dev Utility",
    meta: { title: "Dev Utilities", authRequired: true },
    component: () => import("../views/erp/dev-utility/Index"),
  },

  // ─── Stock Reports ───
  {
    path: "/erp/stock-summary",
    name: "ERP Stock Summary",
    meta: { title: "Stock Summary", authRequired: true },
    component: () => import("../views/erp/stock-reports/Summary"),
  },
  {
    path: "/erp/stock-summary/:stockItemId",
    name: "ERP Stock Monthly Summary",
    meta: { title: "Stock Item Monthly Summary", authRequired: true },
    component: () => import("../views/erp/stock-reports/MonthlySummary"),
  },
  {
    path: "/erp/stock-grouped-summary",
    name: "ERP Grouped Stock Summary",
    meta: { title: "Group-wise Stock Summary", authRequired: true },
    component: () => import("../views/erp/stock-reports/GroupedSummary"),
  },
  {
    path: "/erp/stock-godown-summary",
    name: "ERP Godown Stock Summary",
    meta: { title: "Godown-wise Stock Summary", authRequired: true },
    component: () => import("../views/erp/stock-reports/GodownSummary"),
  },
  {
    path: "/erp/stock-movement-analysis",
    name: "ERP Movement Analysis",
    meta: { title: "Movement Analysis", authRequired: true },
    component: () => import("../views/erp/stock-reports/MovementAnalysis"),
  },
  {
    path: "/erp/stock-ageing",
    name: "ERP Stock Ageing",
    meta: { title: "Stock Ageing Analysis", authRequired: true },
    component: () => import("../views/erp/stock-reports/AgeingAnalysis"),
  },
  {
    path: "/erp/stock-unit-details",
    name: "ERP Stock Unit Details",
    meta: { title: "QR Code / Unit Detail Report", authRequired: true },
    component: () => import("../views/erp/stock-reports/UnitDetailReport"),
  },

  // ─── Statement of Accounts Reports ───
  {
    path: "/erp/reports/outstanding/receivables",
    name: "ERP Receivables",
    meta: { title: "Outstanding Receivables", authRequired: true, outstandingType: "receivables" },
    component: () => import("../views/erp/reports/OutstandingList"),
  },
  {
    path: "/erp/reports/outstanding/payables",
    name: "ERP Payables",
    meta: { title: "Outstanding Payables", authRequired: true, outstandingType: "payables" },
    component: () => import("../views/erp/reports/OutstandingList"),
  },
  {
    path: "/erp/reports/outstanding/:partyLedgerId/bills",
    name: "ERP Party Bills",
    meta: { title: "Party Bills", authRequired: true },
    component: () => import("../views/erp/reports/PartyBills"),
  },
  {
    path: "/erp/reports/ageing",
    name: "ERP Ageing Analysis",
    meta: { title: "Ageing Analysis", authRequired: true },
    component: () => import("../views/erp/reports/AgeingAnalysis"),
  },
  {
    path: "/erp/reports/payment-receipt-register",
    name: "ERP Payment Receipt Register",
    meta: { title: "Payment / Receipt Register", authRequired: true },
    component: () => import("../views/erp/reports/PaymentReceiptRegister"),
  },
  {
    path: "/erp/reports/adjustment-mapping",
    name: "ERP Adjustment Mapping",
    meta: { title: "Adjustment Mapping", authRequired: true },
    component: () => import("../views/erp/reports/AdjustmentMapping"),
  },
  {
    path: "/erp/reports/cost-center-allocation",
    name: "ERP Cost Center Allocation",
    meta: { title: "Cost Center Allocation", authRequired: true },
    component: () => import("../views/erp/reports/cost-center-allocation.vue"),
  },
  {
    path: "/erp/reports/pending-invoicing",
    name: "ERP Pending Invoicing",
    meta: { title: "Pending Billing (DCs)", authRequired: true },
    component: () => import("../views/erp/reports/pending-invoicing.vue"),
  },
  {
    path: "/erp/reports/pending-billing",
    name: "ERP Pending Billing",
    meta: { title: "Pending Invoicing (GRNs)", authRequired: true },
    component: () => import("../views/erp/reports/pending-billing.vue"),
  },
  {
    path: "/erp/reports/outstanding/summary",
    name: "ERP Outstanding Summary",
    meta: { title: "Outstanding Summary", authRequired: true },
    component: () => import("../views/erp/reports/OutstandingSummary"),
  },
  {
    path: "/erp/reports/ledger-statement",
    name: "ERP Ledger Statement",
    meta: { title: "Ledger Statement", authRequired: true },
    component: () => import("../views/erp/reports/LedgerStatement"),
  },
  {
    path: "/erp/reports/group-summary",
    name: "ERP Group Summary",
    meta: { title: "Group Summary", authRequired: true },
    component: () => import("../views/erp/reports/GroupSummary"),
  },

  // ─── Accounting Reports ───
  {
    path: "/erp/trial-balance",
    name: "ERP Trial Balance",
    meta: { title: "Trial Balance", authRequired: true },
    component: () => import("../views/erp/accounting-reports/TrialBalance"),
  },
  {
    path: "/erp/day-book",
    name: "ERP Day Book",
    meta: { title: "Day Book", authRequired: true },
    component: () => import("../views/erp/accounting-reports/DayBook"),
  },
  {
    path: "/erp/balance-sheet",
    name: "ERP Balance Sheet",
    meta: { title: "Balance Sheet", authRequired: true },
    component: () => import("../views/erp/accounting-reports/BalanceSheet"),
  },
  {
    path: "/erp/profit-loss",
    name: "ERP Profit & Loss",
    meta: { title: "Profit & Loss Account", authRequired: true },
    component: () => import("../views/erp/accounting-reports/ProfitLoss"),
  },

  // ─── Phase 3: Transactions ───
  {
    path: "/erp/vouchers",
    name: "ERP Vouchers",
    meta: { title: "Vouchers", authRequired: true },
    component: () => import("../views/erp/vouchers/List"),
  },
  {
    path: "/erp/vouchers/create",
    name: "ERP Voucher Create",
    meta: { title: "Create Voucher", authRequired: true },
    component: () => import("../views/erp/vouchers/Form"),
  },
  {
    path: "/erp/vouchers/:id/print",
    name: "ERP Voucher Print",
    meta: { title: "Print Voucher", authRequired: true },
    component: () => import("../views/erp/vouchers/print/VoucherPrint"),
  },
  {
    path: "/erp/vouchers/:id",
    name: "ERP Voucher Edit",
    meta: { title: "Edit Voucher", authRequired: true },
    component: () => import("../views/erp/vouchers/Form"),
  },
  // ─── Approval System ───
  {
    path: "/erp/approval-config",
    name: "Approval Config",
    meta: { title: "Approval Configuration", authRequired: true },
    component: () => import("../views/erp/approval-config/GroupList"),
  },
  {
    path: "/erp/approval-config/rules/create",
    name: "Approval Rule Create",
    meta: { title: "Create Approval Rule", authRequired: true },
    component: () => import("../views/erp/approval-config/RuleForm"),
  },
  {
    path: "/erp/approval-config/rules/:id/edit",
    name: "Approval Rule Edit",
    meta: { title: "Edit Approval Rule", authRequired: true },
    component: () => import("../views/erp/approval-config/RuleForm"),
  },
  {
    path: "/erp/approvals/pending",
    name: "Pending Approvals",
    meta: { title: "Pending Approvals", authRequired: true },
    component: () => import("../views/erp/approvals/PendingList"),
  },
  // ─── Administration: Users & Roles ───
  {
    path: "/erp/users",
    name: "Admin Users",
    meta: { title: "Users", authRequired: true },
    component: () => import("../views/erp/users/List"),
  },
  {
    path: "/erp/users/create",
    name: "Admin User Create",
    meta: { title: "Create User", authRequired: true },
    component: () => import("../views/erp/users/Form"),
  },
  {
    path: "/erp/users/:id/edit",
    name: "Admin User Edit",
    meta: { title: "Edit User", authRequired: true },
    component: () => import("../views/erp/users/Form"),
  },
  {
    path: "/erp/roles",
    name: "Admin Roles",
    meta: { title: "Roles", authRequired: true },
    component: () => import("../views/erp/roles/List"),
  },
  {
    path: "/erp/roles/:id/permissions",
    name: "Admin Role Permissions",
    meta: { title: "Role Permissions", authRequired: true },
    component: () => import("../views/erp/roles/Permissions"),
  },

  // ─── Store Purchase Requisitions ───
  {
    path: "/erp/store/purchase-requisitions",
    name: "Store Purchase Requisitions",
    meta: { title: "Purchase Requisitions", authRequired: true },
    component: () => import("../views/erp/store/purchase-requisitions/List.vue"),
  },
  {
    path: "/erp/store/purchase-requisitions/new",
    name: "Store Purchase Requisition Create",
    meta: { title: "New Purchase Requisition", authRequired: true },
    component: () => import("../views/erp/store/purchase-requisitions/Form.vue"),
  },
  {
    path: "/erp/store/purchase-requisitions/:id",
    name: "Store Purchase Requisition Details",
    meta: { title: "Requisition Details", authRequired: true },
    component: () => import("../views/erp/store/purchase-requisitions/Details.vue"),
  },
  {
    path: "/erp/store/purchase-requisitions/:id/edit",
    name: "Store Purchase Requisition Edit",
    meta: { title: "Edit Requisition", authRequired: true },
    component: () => import("../views/erp/store/purchase-requisitions/Form.vue"),
  },

  // ─── Store Purchase Orders (separated) ───
  {
    path: "/erp/store/purchase-orders",
    name: "Store Purchase Orders",
    meta: { title: "Store Purchase Orders", authRequired: true },
    component: () => import("../views/erp/store/purchase-orders/List.vue"),
  },
  {
    path: "/erp/store/purchase-orders/new",
    name: "Store Purchase Order Create",
    meta: { title: "New Store PO", authRequired: true },
    component: () => import("../views/erp/store/purchase-orders/Form.vue"),
  },
  {
    path: "/erp/store/purchase-orders/:id",
    name: "Store Purchase Order Details",
    meta: { title: "Store PO Details", authRequired: true },
    component: () => import("../views/erp/store/purchase-orders/Details.vue"),
  },
  {
    path: "/erp/store/purchase-orders/:id/edit",
    name: "Store Purchase Order Edit",
    meta: { title: "Edit Store PO", authRequired: true },
    component: () => import("../views/erp/store/purchase-orders/Form.vue"),
  },

  // ─── Store Sales Orders (separated) ───
  {
    path: "/erp/store/sales-orders",
    name: "Store Sales Orders",
    meta: { title: "Store Sales Orders", authRequired: true },
    component: () => import("../views/erp/store/sales-orders/List.vue"),
  },
  {
    path: "/erp/store/sales-orders/new",
    name: "Store Sales Order Create",
    meta: { title: "New Store SO", authRequired: true },
    component: () => import("../views/erp/store/sales-orders/Form.vue"),
  },
  {
    path: "/erp/store/sales-orders/:id",
    name: "Store Sales Order Details",
    meta: { title: "Store SO Details", authRequired: true },
    component: () => import("../views/erp/store/sales-orders/Details.vue"),
  },
  {
    path: "/erp/store/sales-orders/:id/edit",
    name: "Store Sales Order Edit",
    meta: { title: "Edit Store SO", authRequired: true },
    component: () => import("../views/erp/store/sales-orders/Form.vue"),
  },

  // ─── Store GRN ───
  {
    path: "/erp/store/grn",
    name: "Store GRN",
    meta: { title: "Goods Receipt Notes", authRequired: true },
    component: () => import("../views/erp/store/grn/List.vue"),
  },
  {
    path: "/erp/store/grn/new",
    name: "Store GRN Create",
    meta: { title: "New GRN", authRequired: true },
    component: () => import("../views/erp/store/grn/Form.vue"),
  },
  {
    path: "/erp/store/grn/:id",
    name: "Store GRN Details",
    meta: { title: "GRN Details", authRequired: true },
    component: () => import("../views/erp/store/grn/Details.vue"),
  },
  {
    path: "/erp/store/grn/:id/edit",
    name: "Store GRN Edit",
    meta: { title: "Edit GRN", authRequired: true },
    component: () => import("../views/erp/store/grn/Form.vue"),
  },

  // ─── Store Material Issue ───
  {
    path: "/erp/store/material-issue",
    name: "Store Material Issue",
    meta: { title: "Material Issues", authRequired: true },
    component: () => import("../views/erp/store/material-issue/List.vue"),
  },
  {
    path: "/erp/store/material-issue/new",
    name: "Store Material Issue Create",
    meta: { title: "New Material Issue", authRequired: true },
    component: () => import("../views/erp/store/material-issue/Form.vue"),
  },
  {
    path: "/erp/store/material-issue/:id",
    name: "Store Material Issue Details",
    meta: { title: "Material Issue Details", authRequired: true },
    component: () => import("../views/erp/store/material-issue/Details.vue"),
  },
  {
    path: "/erp/store/material-issue/:id/edit",
    name: "Store Material Issue Edit",
    meta: { title: "Edit Material Issue", authRequired: true },
    component: () => import("../views/erp/store/material-issue/Form.vue"),
  },
  // ─── Store LIS BOM (auto-consumption against LIS bookings) ───
  {
    path: "/erp/store/lis-bom",
    name: "Store LIS BOM",
    meta: { title: "LIS BOM", authRequired: true },
    component: () => import("../views/erp/store/lis-bom/List.vue"),
  },
  {
    path: "/erp/store/lis-bom/new",
    name: "Store LIS BOM Create",
    meta: { title: "New LIS BOM", authRequired: true },
    component: () => import("../views/erp/store/lis-bom/Form.vue"),
  },
  {
    path: "/erp/store/lis-bom/:id",
    name: "Store LIS BOM Details",
    meta: { title: "LIS BOM Details", authRequired: true },
    component: () => import("../views/erp/store/lis-bom/Details.vue"),
  },
  {
    path: "/erp/store/lis-bom/:id/edit",
    name: "Store LIS BOM Edit",
    meta: { title: "Edit LIS BOM", authRequired: true },
    component: () => import("../views/erp/store/lis-bom/Form.vue"),
  },

  {
    path: "/erp/store/physical-stock-verification",
    name: "Store Physical Stock Verification",
    meta: { title: "Physical Stock Verification", authRequired: true },
    component: () => import("../views/erp/store/physical-stock-verification/List.vue"),
  },
  {
    path: "/erp/store/physical-stock-verification/new",
    name: "Store PSV Create",
    meta: { title: "New Physical Stock Verification", authRequired: true },
    component: () => import("../views/erp/store/physical-stock-verification/Form.vue"),
  },
  {
    path: "/erp/store/physical-stock-verification/:id",
    name: "Store PSV Details",
    meta: { title: "PSV Details", authRequired: true },
    component: () => import("../views/erp/store/physical-stock-verification/Details.vue"),
  },
  {
    path: "/erp/store/physical-stock-verification/:id/edit",
    name: "Store PSV Edit",
    meta: { title: "Edit Physical Stock Verification", authRequired: true },
    component: () => import("../views/erp/store/physical-stock-verification/Form.vue"),
  },

  // ─── Gate Entry Checklist ───
  {
    path: "/erp/gate-entry-checklist",
    name: "Gate Entry Checklist",
    meta: { title: "Gate Entry Checklist", authRequired: true },
    component: () => import("../views/erp/gate-entry-checklist/List.vue"),
  },

  // ─── Store Gate Pass ───
  {
    path: "/erp/store/gate-pass",
    name: "Store Gate Pass",
    meta: { title: "Gate Passes", authRequired: true },
    component: () => import("../views/erp/store/gate-pass/List.vue"),
  },
  {
    path: "/erp/store/gate-pass/new",
    name: "Store Gate Pass Create",
    meta: { title: "New Gate Pass", authRequired: true },
    component: () => import("../views/erp/store/gate-pass/Form.vue"),
  },
  {
    path: "/erp/store/gate-pass/:id",
    name: "Store Gate Pass Details",
    meta: { title: "Gate Pass Details", authRequired: true },
    component: () => import("../views/erp/store/gate-pass/Details.vue"),
  },
  {
    path: "/erp/store/gate-pass/:id/edit",
    name: "Store Gate Pass Edit",
    meta: { title: "Edit Gate Pass", authRequired: true },
    component: () => import("../views/erp/store/gate-pass/Form.vue"),
  },
  {
    path: "/erp/store/gate-pass/:id/return",
    name: "Store Gate Pass Return",
    meta: { title: "Gate Pass Return", authRequired: true },
    component: () => import("../views/erp/store/gate-pass/ReturnForm.vue"),
  },

  // ─── Store CFQ (Call for Quotation) ───
  {
    path: "/erp/store/cfq",
    name: "Store CFQ",
    meta: { title: "Call for Quotations", authRequired: true },
    component: () => import("../views/erp/store/cfq/List.vue"),
  },
  {
    path: "/erp/store/cfq/new",
    name: "Store CFQ Create",
    meta: { title: "New CFQ", authRequired: true },
    component: () => import("../views/erp/store/cfq/Form.vue"),
  },
  {
    path: "/erp/store/cfq/:id",
    name: "Store CFQ Details",
    meta: { title: "CFQ Details", authRequired: true },
    component: () => import("../views/erp/store/cfq/Details.vue"),
  },
  {
    path: "/erp/store/cfq/:id/edit",
    name: "Store CFQ Edit",
    meta: { title: "Edit CFQ", authRequired: true },
    component: () => import("../views/erp/store/cfq/Form.vue"),
  },
  {
    path: "/erp/store/cfq/:id/vendor-response",
    name: "Store CFQ Vendor Response",
    meta: { title: "CFQ Vendor Response", authRequired: true },
    component: () => import("../views/erp/store/cfq/VendorResponse.vue"),
  },
  {
    path: "/cfq/respond/:accessToken",
    name: "CFQ Vendor Public Response",
    meta: { title: "Submit Quotation", authRequired: false },
    component: () => import("../views/erp/store/cfq/CfqVendorPublicResponse.vue"),
  },
  {
    path: "/gate-pass/reply/:token",
    name: "Gate Pass Public Reply",
    meta: { title: "Gate Pass Reply", authRequired: false },
    component: () => import("../views/erp/store/gate-pass/PublicReply.vue"),
  },

  // ─── Store CS (Comparative Statement) ───
  {
    path: "/erp/store/cs",
    name: "Store CS",
    meta: { title: "Comparative Statements", authRequired: true },
    component: () => import("../views/erp/store/cs/List.vue"),
  },
  {
    path: "/erp/store/cs/new",
    name: "Store CS Create",
    meta: { title: "New CS", authRequired: true },
    component: () => import("../views/erp/store/cs/Form.vue"),
  },
  {
    path: "/erp/store/cs/:id",
    name: "Store CS Details",
    meta: { title: "CS Details", authRequired: true },
    component: () => import("../views/erp/store/cs/Details.vue"),
  },
  {
    path: "/erp/store/cs/:id/edit",
    name: "Store CS Edit",
    meta: { title: "Edit CS", authRequired: true },
    component: () => import("../views/erp/store/cs/Form.vue"),
  },

  // ─── Store Delivery Challan ───
  {
    path: "/erp/store/delivery-challan",
    name: "Store Delivery Challan",
    meta: { title: "Delivery Challans", authRequired: true },
    component: () => import("../views/erp/store/delivery-challan/List.vue"),
  },
  {
    path: "/erp/store/delivery-challan/new",
    name: "Store Delivery Challan Create",
    meta: { title: "New Delivery Challan", authRequired: true },
    component: () => import("../views/erp/store/delivery-challan/Form.vue"),
  },
  {
    path: "/erp/store/delivery-challan/:id",
    name: "Store Delivery Challan Details",
    meta: { title: "Delivery Challan Details", authRequired: true },
    component: () => import("../views/erp/store/delivery-challan/Details.vue"),
  },
  {
    path: "/erp/store/delivery-challan/:id/edit",
    name: "Store Delivery Challan Edit",
    meta: { title: "Edit Delivery Challan", authRequired: true },
    component: () => import("../views/erp/store/delivery-challan/Form.vue"),
  },

  // ─── Store Reports ───
  {
    path: "/erp/store/reports/stock-summary",
    name: "Store Stock Summary",
    meta: { title: "Store Stock Summary", authRequired: true },
    component: () => import("../views/erp/store/reports/StockSummary.vue"),
  },
  {
    path: "/erp/store/reports/gate-pass-pending",
    name: "Store Gate Pass Pending",
    meta: { title: "Gate Pass Pending", authRequired: true },
    component: () => import("../views/erp/store/reports/GatePassPending.vue"),
  },
  {
    path: "/erp/store/reports/consumption",
    name: "Store Consumption Report",
    meta: { title: "Consumption Report", authRequired: true },
    component: () => import("../views/erp/store/reports/ConsumptionReport.vue"),
  },
  {
    path: "/erp/store/reports/cfq-cs-register",
    name: "Store CFQ CS Register",
    meta: { title: "CFQ/CS Register", authRequired: true },
    component: () => import("../views/erp/store/reports/CfqCsRegister.vue"),
  },
  {
    path: "/erp/store/reports/reorder-alerts",
    name: "Store Reorder Alerts",
    meta: { title: "Reorder Alerts", authRequired: true },
    component: () => import("../views/erp/store/reports/ReorderAlerts.vue"),
  },
  {
    path: "/erp/store/reports/near-expiry",
    name: "Store Near Expiry",
    meta: { title: "Near-Expiry Batches", authRequired: true },
    component: () => import("../views/erp/store/reports/NearExpiry.vue"),
  },
  {
    path: "/erp/store/reports/consumption-by-cost-center",
    name: "Store Consumption By Cost Center",
    meta: { title: "Consumption by Cost Center", authRequired: true },
    component: () => import("../views/erp/store/reports/ConsumptionByCostCenter.vue"),
  },
  {
    path: "/erp/store/reports/grn-invoice-match",
    name: "Store GRN Invoice Match",
    meta: { title: "GRN-Invoice Match", authRequired: true },
    component: () => import("../views/erp/store/reports/GrnInvoiceMatch.vue"),
  },
  {
    path: "/erp/store/reports/open-po-register",
    name: "Store Open PO Register",
    meta: { title: "Open PO Register", authRequired: true },
    component: () => import("../views/erp/store/reports/OpenPoRegister.vue"),
  },
  {
    path: "/erp/store/reports/vendor-scores",
    name: "Store Vendor Scores",
    meta: { title: "Vendor Scorecard", authRequired: true },
    component: () => import("../views/erp/store/reports/VendorScores.vue"),
  },

  // ─── Configuration: Email Templates ───
  {
    path: "/erp/configuration/email-templates",
    name: "Email Templates",
    meta: { title: "Email Templates", authRequired: true },
    component: () => import("../views/erp/email-templates/List.vue"),
  },

  // ─── Configuration: Voucher Classes ───
  {
    path: "/erp/configuration/voucher-classes",
    name: "Voucher Classes",
    meta: { title: "Voucher Classes", authRequired: true },
    component: () => import("../views/erp/voucher-classes/List.vue"),
  },

  // ─── Configuration: Module Seeders (SuperAdmin) ───
  {
    path: "/erp/configuration/seeders",
    name: "Module Seeders",
    meta: { title: "Module Seeders", authRequired: true, roles: ["SuperAdmin"] },
    component: () => import("../views/erp/configuration/seeders/Panel.vue"),
  },

  // ─── Batch Recalls (Pharma) ───
  {
    path: "/erp/batch-recalls",
    name: "Batch Recalls",
    meta: { title: "Batch Recalls", authRequired: true },
    component: () => import("../views/erp/batch-recalls/List.vue"),
  },
  {
    path: "/erp/batch-recalls/:id",
    name: "Batch Recall Details",
    meta: { title: "Recall Details", authRequired: true },
    component: () => import("../views/erp/batch-recalls/Details.vue"),
  },

  // ─── POS (Point of Sale) ───
  {
    path: "/erp/pos/terminal",
    name: "POS Terminal",
    meta: { title: "POS Terminal", authRequired: true, workspaceTab: false },
    component: () => import("../views/erp/pos/Terminal.vue"),
  },
  {
    path: "/erp/pos/sessions",
    name: "POS Sessions",
    meta: { title: "POS Sessions", authRequired: true },
    component: () => import("../views/erp/pos/Sessions.vue"),
  },
  {
    path: "/erp/pos/sessions/:id",
    name: "POS Z-Report",
    meta: { title: "POS Z-Report", authRequired: true },
    component: () => import("../views/erp/pos/ZReport.vue"),
  },

  // ---- Store Items (Master) ----
  {
    path: "/erp/store/items",
    name: "Store Items",
    meta: { title: "Store Items", authRequired: true },
    component: () => import("../views/erp/store/items/List.vue"),
  },
  {
    path: "/erp/store/items/create",
    name: "Store Item Create",
    meta: { title: "New Store Item", authRequired: true },
    component: () => import("../views/erp/store/items/Form.vue"),
  },
  {
    path: "/erp/store/items/:id",
    name: "Store Item Edit",
    meta: { title: "Edit Store Item", authRequired: true },
    component: () => import("../views/erp/store/items/Form.vue"),
  },

  // ---- Audit Logs ----
  {
    path: "/erp/audit-logs",
    name: "ERP Audit Logs",
    meta: { title: "Audit Logs", authRequired: true },
    component: () => import("../views/erp/audit-logs/List.vue"),
  },

  // ---- Project Settings (Admin) ----
  {
    path: "/erp/project-settings",
    name: "Project Settings",
    meta: { title: "Project Settings", authRequired: true },
    component: () => import("../views/erp/project-settings/Index.vue"),
  },

  // ═══════════════ Job Consultancy ═══════════════
  {
    path: "/erp/jc/masters/qualification-types",
    name: "ERP JC Qualification Types",
    meta: { title: "Qualification Types", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/QualificationTypeList"),
  },
  {
    path: "/erp/jc/masters/industry-types",
    name: "ERP JC Industry Types",
    meta: { title: "Industry Types", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/IndustryTypeList"),
  },
  {
    path: "/erp/jc/masters/job-titles",
    name: "ERP JC Job Titles",
    meta: { title: "Job Titles", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/JobTitleList"),
  },
  {
    path: "/erp/jc/masters/qualifications",
    name: "ERP JC Qualifications",
    meta: { title: "Qualifications", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/QualificationList"),
  },
  {
    path: "/erp/jc/masters/job-types",
    name: "ERP JC Job Types",
    meta: { title: "Job Types", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/JobTypeList"),
  },
  {
    path: "/erp/jc/masters/interview-conditions",
    name: "ERP JC Interview Conditions",
    meta: { title: "Interview Conditions", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/InterviewConditionList"),
  },
  {
    path: "/erp/jc/masters/placed-counts",
    name: "ERP JC Placed Counts",
    meta: { title: "Placed Counts", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/PlacedCountList"),
  },
  {
    path: "/erp/jc/masters/profile-highlights",
    name: "ERP JC Profile Highlights",
    meta: { title: "Profile Highlights", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/ProfileHighlightList"),
  },
  {
    path: "/erp/jc/masters/per-day-amount",
    name: "ERP JC Per Day Amount",
    meta: { title: "Per Day Amount", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/PerDayAmountSettings"),
  },
  {
    path: "/erp/jc/masters/accounting-ledger-bindings",
    name: "ERP JC Accounting Ledger Bindings",
    meta: { title: "Accounting Ledger Bindings", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/AccountingLedgerBindings"),
  },
  {
    path: "/erp/jc/masters/otp-records",
    name: "ERP JC OTP Records",
    meta: { title: "OTP Records", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/OtpRecordList"),
  },
  {
    path: "/erp/jc/users",
    name: "ERP JC Users",
    meta: { title: "JC Users", authRequired: true },
    component: () => import("../views/erp/job-consultancy/masters/UserList"),
  },
  {
    path: "/erp/jc/employers",
    name: "ERP JC Employers",
    meta: { title: "Employers", authRequired: true },
    component: () => import("../views/erp/job-consultancy/employers/List"),
  },
  {
    path: "/erp/jc/employers/create",
    name: "ERP JC Employer Create",
    meta: { title: "Create Employer", authRequired: true },
    component: () => import("../views/erp/job-consultancy/employers/Form"),
  },
  {
    path: "/erp/jc/employers/:id",
    name: "ERP JC Employer Edit",
    meta: { title: "Edit Employer", authRequired: true },
    component: () => import("../views/erp/job-consultancy/employers/Form"),
  },
  {
    path: "/erp/jc/employers/:id/details",
    name: "ERP JC Employer Details",
    meta: { title: "Employer Details", authRequired: true },
    component: () => import("../views/erp/job-consultancy/employers/Details"),
  },

  // JC Seekers
  {
    path: "/erp/jc/seekers",
    name: "ERP JC Seekers",
    meta: { title: "Job Seekers", authRequired: true },
    component: () => import("../views/erp/job-consultancy/seekers/List"),
  },
  {
    path: "/erp/jc/seekers/expert-verifications",
    name: "ERP JC Expert Verification Request",
    meta: { title: "Expert Verification Request", authRequired: true },
    component: () => import("../views/erp/job-consultancy/seekers/ExpertVerificationQueue"),
  },
  {
    path: "/erp/jc/seekers/highlight-orders",
    name: "ERP JC Highlight Orders",
    meta: { title: "Profile Highlight Orders", authRequired: true },
    component: () => import("../views/erp/job-consultancy/seekers/HighlightOrdersQueue"),
  },
  {
    path: "/erp/jc/seekers/feedback-orders",
    name: "ERP JC Feedback Orders",
    meta: { title: "Feedback Verified Orders", authRequired: true },
    component: () => import("../views/erp/job-consultancy/seekers/FeedbackOrdersQueue"),
  },
  {
    path: "/erp/jc/seekers/create",
    name: "ERP JC Seeker Create",
    meta: { title: "Create Seeker", authRequired: true },
    component: () => import("../views/erp/job-consultancy/seekers/Form"),
  },
  {
    path: "/erp/jc/seekers/:id/details",
    name: "ERP JC Seeker Details",
    meta: { title: "Seeker Details", authRequired: true },
    component: () => import("../views/erp/job-consultancy/seekers/Details"),
  },
  {
    path: "/erp/jc/seekers/:id",
    name: "ERP JC Seeker Edit",
    meta: { title: "Edit Seeker", authRequired: true },
    component: () => import("../views/erp/job-consultancy/seekers/Form"),
  },

  // ---- JC Job Posts ----
  {
    path: "/erp/jc/job-posts",
    name: "ERP JC Job Posts",
    meta: { title: "Job Posts", authRequired: true },
    component: () => import("../views/erp/job-consultancy/job-posts/List"),
  },

  // ---- JC Applications ----
  {
    path: "/erp/jc/applications",
    name: "ERP JC Applications",
    meta: { title: "Applications", authRequired: true },
    component: () => import("../views/erp/job-consultancy/applications/List"),
  },

  // ---- JC Interviews ----
  {
    path: "/erp/jc/interviews",
    name: "ERP JC Interviews",
    meta: { title: "Interviews", authRequired: true },
    component: () => import("../views/erp/job-consultancy/interviews/List"),
  },
  {
    path: "/erp/jc/interviews/create",
    name: "ERP JC Interview Create",
    meta: { title: "Schedule Interview", authRequired: true },
    component: () => import("../views/erp/job-consultancy/interviews/Form"),
  },
  {
    path: "/erp/jc/interviews/:id",
    name: "ERP JC Interview Edit",
    meta: { title: "Edit Interview", authRequired: true },
    component: () => import("../views/erp/job-consultancy/interviews/Form"),
  },

  // ---- JC Interview Questions ----
  {
    path: "/erp/jc/interview-questions",
    name: "ERP JC Interview Questions",
    meta: { title: "Interview Questions", authRequired: true },
    component: () => import("../views/erp/job-consultancy/interview-questions/List"),
  },

  // ---- JC Panelist Requests ----
  {
    path: "/erp/jc/panelist-requests",
    name: "ERP JC Panelist Requests",
    meta: { title: "Panelist Requests", authRequired: true },
    component: () => import("../views/erp/job-consultancy/panelist-requests/List"),
  },

  // ---- JC Interview Feedbacks ----
  {
    path: "/erp/jc/interview-feedbacks",
    name: "ERP JC Interview Feedbacks",
    meta: { title: "Interview Feedback", authRequired: true },
    component: () => import("../views/erp/job-consultancy/interview-feedbacks/List"),
  },

  // ---- JC Candidate Selections ----
  {
    path: "/erp/jc/candidate-selections",
    name: "ERP JC Candidate Selections",
    meta: { title: "Candidate Selections", authRequired: true },
    component: () => import("../views/erp/job-consultancy/candidate-selections/List"),
  },

  // ---- JC Panelists ----
  {
    path: "/erp/jc/panelists",
    name: "ERP JC Panelists",
    meta: { title: "Panelists", authRequired: true },
    component: () => import("../views/erp/job-consultancy/panelists/List"),
  },

  // ---- JC Consultancy ----
  {
    path: "/erp/jc/consultancy",
    name: "ERP JC Consultancies",
    meta: { title: "Consultancies", authRequired: true },
    component: () => import("../views/erp/job-consultancy/consultancy/List"),
  },
  {
    path: "/erp/jc/consultancy/create",
    name: "ERP JC Consultancy Create",
    meta: { title: "Create Consultancy", authRequired: true },
    component: () => import("../views/erp/job-consultancy/consultancy/Form"),
  },
  {
    path: "/erp/jc/consultancy/dashboard",
    name: "ERP JC Consultancy Dashboard",
    meta: { title: "Consultancy Dashboard", authRequired: true },
    component: () => import("../views/erp/job-consultancy/consultancy/Dashboard"),
  },
  {
    path: "/erp/jc/consultancy/:id",
    name: "ERP JC Consultancy Edit",
    meta: { title: "Edit Consultancy", authRequired: true },
    component: () => import("../views/erp/job-consultancy/consultancy/Form"),
  },

  // ---- JC Wallet ----
  {
    path: "/erp/jc/wallet",
    name: "ERP JC Wallet",
    meta: { title: "Wallet", authRequired: true },
    component: () => import("../views/erp/job-consultancy/wallet/Dashboard"),
  },

  // ---- JC Plans ----
  {
    path: "/erp/jc/plans",
    name: "ERP JC Plans",
    meta: { title: "Plans", authRequired: true },
    component: () => import("../views/erp/job-consultancy/plans/List"),
  },

  // ---- JC Payments ----
  {
    path: "/erp/jc/payments",
    name: "ERP JC Payments",
    meta: { title: "Payment Transactions", authRequired: true },
    component: () => import("../views/erp/job-consultancy/payments/List"),
  },

  // ---- JC Leads (CRM) ----
  {
    path: "/erp/jc/leads",
    name: "ERP JC Leads",
    meta: { title: "Leads", authRequired: true },
    component: () => import("../views/erp/job-consultancy/leads/List"),
  },
  {
    path: "/erp/jc/leads/create",
    name: "ERP JC Lead Create",
    meta: { title: "Create Lead", authRequired: true },
    component: () => import("../views/erp/job-consultancy/leads/Form"),
  },
  {
    path: "/erp/jc/leads/:id/call-log",
    name: "ERP JC Lead Call Log",
    meta: { title: "Call Log", authRequired: true },
    component: () => import("../views/erp/job-consultancy/leads/CallLog"),
  },
  {
    path: "/erp/jc/leads/:id",
    name: "ERP JC Lead Edit",
    meta: { title: "Edit Lead", authRequired: true },
    component: () => import("../views/erp/job-consultancy/leads/Form"),
  },

  // ---- JC CMS ----
  {
    path: "/erp/jc/cms/blogs",
    name: "ERP JC CMS Blogs",
    meta: { title: "Blog Posts", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/Blogs"),
  },
  {
    path: "/erp/jc/cms/testimonials",
    name: "ERP JC CMS Testimonials",
    meta: { title: "Testimonials", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/Testimonials"),
  },
  {
    path: "/erp/jc/cms/menus",
    name: "ERP JC CMS Menus",
    meta: { title: "Menu Management", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/Menus"),
  },
  {
    path: "/erp/jc/cms/home-flashes",
    name: "ERP JC CMS Home Flashes",
    meta: { title: "Home Flashes", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/HomeFlashes"),
  },
  {
    path: "/erp/jc/cms/client-logos",
    name: "ERP JC CMS Client Logos",
    meta: { title: "Client Logos", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/ClientLogos"),
  },
  {
    path: "/erp/jc/cms/contact-info",
    name: "ERP JC CMS Contact Info",
    meta: { title: "Contact Us", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/ContactInfo"),
  },
  {
    path: "/erp/jc/cms/photos",
    name: "ERP JC CMS Photos",
    meta: { title: "Photo Gallery", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/Photos"),
  },
  {
    path: "/erp/jc/cms/advertisements",
    name: "ERP JC CMS Advertisements",
    meta: { title: "Advertisements", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/Advertisements"),
  },
  {
    path: "/erp/jc/cms/faqs",
    name: "ERP JC CMS FAQs",
    meta: { title: "FAQs", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/Faqs"),
  },
  {
    path: "/erp/jc/cms/team-members",
    name: "ERP JC CMS Team Members",
    meta: { title: "Team Members", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/TeamMembers"),
  },
  {
    path: "/erp/jc/cms/job-categories",
    name: "ERP JC CMS Job Categories",
    meta: { title: "Job Categories", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/JobCategories"),
  },
  {
    path: "/erp/jc/cms/site-settings",
    name: "ERP JC CMS Site Settings",
    meta: { title: "Site Settings", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/SiteSettings"),
  },
  {
    path: "/erp/jc/cms/contact-messages",
    name: "ERP JC CMS Contact Messages",
    meta: { title: "Contact Messages", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/ContactMessages"),
  },
  {
    path: "/erp/jc/cms/expert-applications",
    name: "ERP JC CMS Expert Applications",
    meta: { title: "Expert Applications", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/ExpertApplications"),
  },
  {
    path: "/erp/jc/cms/newsletter-subscriptions",
    name: "ERP JC CMS Newsletter",
    meta: { title: "Newsletter Subscriptions", authRequired: true },
    component: () => import("../views/erp/job-consultancy/cms/NewsletterSubscriptions"),
  },

  // ---- JC Messages ----
  {
    path: "/erp/jc/messages",
    name: "ERP JC Messages",
    meta: { title: "Messages", authRequired: true },
    component: () => import("../views/erp/job-consultancy/messages/Conversations"),
  },

  // ---- JC Admin Dashboard ----
  {
    path: "/erp/jc/dashboard",
    name: "ERP JC Dashboard",
    meta: { title: "JC Dashboard", authRequired: true },
    component: () => import("../views/erp/job-consultancy/dashboard/AdminDashboard"),
  },
  {
    path: "/erp/jc/dashboard/seeker",
    name: "ERP JC Seeker Dashboard",
    meta: { title: "Seeker Dashboard", authRequired: true },
    component: () => import("../views/erp/job-consultancy/dashboard/SeekerDashboard"),
  },
  {
    path: "/erp/jc/search-jobs",
    name: "ERP JC Search Jobs",
    meta: { title: "Search Jobs", authRequired: true },
    component: () => import("../views/erp/job-consultancy/search-jobs/SearchJobs"),
  },
  {
    path: "/erp/jc/seeker/search-jobs",
    name: "ERP JC Seeker Search Jobs",
    meta: { title: "Search Jobs", authRequired: true },
    component: () => import("../views/erp/job-consultancy/search-jobs/SeekerSearchJobs"),
  },

  // ---- JC Reports ----
  {
    path: "/erp/jc/reports/seekers",
    name: "ERP JC Report Seekers",
    meta: { title: "Seeker Report", authRequired: true },
    component: () => import("../views/erp/job-consultancy/reports/SeekerReport"),
  },
  {
    path: "/erp/jc/reports/employers",
    name: "ERP JC Report Employers",
    meta: { title: "Employer Report", authRequired: true },
    component: () => import("../views/erp/job-consultancy/reports/EmployerReport"),
  },
  {
    path: "/erp/jc/reports/job-posts",
    name: "ERP JC Report Job Posts",
    meta: { title: "Job Post Report", authRequired: true },
    component: () => import("../views/erp/job-consultancy/reports/JobPostReport"),
  },
  {
    path: "/erp/jc/reports/applications",
    name: "ERP JC Report Applications",
    meta: { title: "Application Report", authRequired: true },
    component: () => import("../views/erp/job-consultancy/reports/ApplicationReport"),
  },
  {
    path: "/erp/jc/reports/interviews",
    name: "ERP JC Report Interviews",
    meta: { title: "Interview Report", authRequired: true },
    component: () => import("../views/erp/job-consultancy/reports/InterviewReport"),
  },
  {
    path: "/erp/jc/reports/payments",
    name: "ERP JC Report Payments",
    meta: { title: "Payment Report", authRequired: true },
    component: () => import("../views/erp/job-consultancy/reports/PaymentReport"),
  },
  {
    path: "/erp/jc/reports/collections",
    name: "ERP JC Report Collections",
    meta: { title: "Collection Report", authRequired: true },
    component: () => import("../views/erp/job-consultancy/reports/CollectionReport"),
  },
  {
    path: "/erp/jc/reports/leads",
    name: "ERP JC Report Leads",
    meta: { title: "Lead Report", authRequired: true },
    component: () => import("../views/erp/job-consultancy/reports/LeadReport"),
  },
  {
    path: "/erp/jc/reports/consultancy-jobs",
    name: "ERP JC Report Consultancy Jobs",
    meta: { title: "Consultancy Job Report", authRequired: true },
    component: () => import("../views/erp/job-consultancy/reports/ConsultancyReport"),
  },
  {
    path: "/erp/jc/migration",
    name: "ERP JC Migration",
    meta: { title: "JC Data Migration Tool", authRequired: true },
    component: () => import("../views/erp/job-consultancy/migration/MigrationTool.vue"),
  },

  // ═══════════════ Matrimony ═══════════════
  {
    path: "/erp/mm/masters",
    name: "ERP MM Masters",
    meta: { title: "Matrimony Masters", authRequired: true },
    component: () => import("../views/erp/matrimony/masters/MatrimonyMasters.vue"),
  },
  {
    path: "/erp/mm/profiles",
    name: "ERP MM Profiles",
    meta: { title: "Profiles", authRequired: true },
    component: () => import("../views/erp/matrimony/profiles/ProfileList.vue"),
  },
  {
    path: "/erp/mm/profiles/new",
    name: "ERP MM Profile Create",
    meta: { title: "New Profile", authRequired: true },
    component: () => import("../views/erp/matrimony/profiles/ProfileForm.vue"),
  },
  {
    path: "/erp/mm/profiles/:id/edit",
    name: "ERP MM Profile Edit",
    meta: { title: "Edit Profile", authRequired: true },
    component: () => import("../views/erp/matrimony/profiles/ProfileForm.vue"),
  },
  {
    path: "/erp/mm/profiles/:id/details",
    name: "ERP MM Profile Details",
    meta: { title: "Profile Details", authRequired: true },
    component: () => import("../views/erp/matrimony/profiles/ProfileDetails.vue"),
  },
  {
    path: "/erp/mm/vendors",
    name: "ERP MM Vendors",
    meta: { title: "Vendors", authRequired: true },
    component: () => import("../views/erp/matrimony/vendors/VendorList.vue"),
  },
  {
    path: "/erp/mm/vendors/register",
    name: "ERP MM Vendor Register",
    meta: { title: "Register Vendor", authRequired: true },
    component: () => import("../views/erp/matrimony/vendors/VendorRegister.vue"),
  },
  {
    path: "/erp/mm/vendors/:id/details",
    name: "ERP MM Vendor Details",
    meta: { title: "Vendor Details", authRequired: true },
    component: () => import("../views/erp/matrimony/vendors/VendorDetails.vue"),
  },
  {
    path: "/erp/mm/vendors/:id/edit",
    name: "ERP MM Vendor Edit",
    meta: { title: "Edit Vendor", authRequired: true },
    component: () => import("../views/erp/matrimony/vendors/VendorEdit.vue"),
  },
  {
    path: "/erp/mm/photos/queue",
    name: "ERP MM Photo Queue",
    meta: { title: "Photo Approval Queue", authRequired: true },
    component: () => import("../views/erp/matrimony/profiles/PhotoQueue.vue"),
  },
  {
    path: "/erp/mm/documents/queue",
    name: "ERP MM Document Queue",
    meta: { title: "Document Verification Queue", authRequired: true },
    component: () => import("../views/erp/matrimony/profiles/DocQueue.vue"),
  },
  {
    path: "/erp/mm/audit/interests",
    name: "ERP MM Interests Audit",
    meta: { title: "Interests Audit", authRequired: true },
    component: () => import("../views/erp/matrimony/audit/InterestsAudit.vue"),
  },
  {
    path: "/erp/mm/audit/messages",
    name: "ERP MM Messages Audit",
    meta: { title: "Messages Audit", authRequired: true },
    component: () => import("../views/erp/matrimony/audit/MessagesAudit.vue"),
  },
  {
    path: "/erp/mm/plans",
    name: "ERP MM Plans",
    meta: { title: "Matrimony Plans", authRequired: true },
    component: () => import("../views/erp/matrimony/plans/PlanList.vue"),
  },
  {
    path: "/erp/mm/subscriptions",
    name: "ERP MM Subscriptions",
    meta: { title: "Subscriptions", authRequired: true },
    component: () => import("../views/erp/matrimony/plans/SubscriptionList.vue"),
  },
  {
    path: "/erp/mm/payments",
    name: "ERP MM Payments",
    meta: { title: "Payments", authRequired: true },
    component: () => import("../views/erp/matrimony/plans/PaymentList.vue"),
  },
  {
    path: "/erp/mm/dashboard",
    name: "ERP MM Dashboard",
    meta: { title: "Matrimony Dashboard", authRequired: true },
    component: () => import("../views/erp/matrimony/dashboard/MatrimonyDashboard.vue"),
  },
  {
    path: "/erp/mm/reports",
    name: "ERP MM Reports",
    meta: { title: "Matrimony Reports", authRequired: true },
    component: () => import("../views/erp/matrimony/dashboard/MatrimonyReports.vue"),
  },
  {
    path: "/erp/mm/migration",
    name: "ERP MM Migration",
    meta: { title: "Data Migration Tool", authRequired: true },
    component: () => import("../views/erp/matrimony/migration/MigrationTool.vue"),
  },
  {
    path: "/erp/mm/settings/captcha",
    name: "ERP MM Captcha Settings",
    meta: { title: "Captcha Settings", authRequired: true, permission: "mm-settings" },
    component: () => import("../views/erp/matrimony/settings/Captcha.vue"),
  },

  // ═══════════════ Website CMS ═══════════════
  {
    path: "/website/gallery",
    name: "Website Gallery List",
    meta: { title: "Photo Gallery Master", authRequired: true },
    component: () => import("../views/website/gallery/GalleryList.vue"),
  },
  {
    path: "/website/gallery/add",
    name: "Website Gallery Add",
    meta: { title: "Add Gallery Item", authRequired: true },
    component: () => import("../views/website/gallery/GalleryForm.vue"),
  },
  {
    path: "/website/gallery/edit/:id",
    name: "Website Gallery Edit",
    meta: { title: "Edit Gallery Item", authRequired: true },
    component: () => import("../views/website/gallery/GalleryForm.vue"),
  },
  {
    path: "/website/blog",
    name: "Website Blog List",
    meta: { title: "Blog Master", authRequired: true },
    component: () => import("../views/website/blog/BlogList.vue"),
  },
  {
    path: "/website/blog/add",
    name: "Website Blog Add",
    meta: { title: "Add Blog Post", authRequired: true },
    component: () => import("../views/website/blog/BlogForm.vue"),
  },
  {
    path: "/website/blog/edit/:id",
    name: "Website Blog Edit",
    meta: { title: "Edit Blog Post", authRequired: true },
    component: () => import("../views/website/blog/BlogForm.vue"),
  },
  {
    path: "/website/faqs",
    name: "Website FAQ List",
    meta: { title: "FAQ Master", authRequired: true },
    component: () => import("../views/website/faqs/FaqList.vue"),
  },
  {
    path: "/website/faqs/add",
    name: "Website FAQ Add",
    meta: { title: "Add FAQ", authRequired: true },
    component: () => import("../views/website/faqs/FaqForm.vue"),
  },
  {
    path: "/website/faqs/edit/:id",
    name: "Website FAQ Edit",
    meta: { title: "Edit FAQ", authRequired: true },
    component: () => import("../views/website/faqs/FaqForm.vue"),
  },
  {
    path: "/website/events",
    name: "Website Event List",
    meta: { title: "Latest Happening - NEWS", authRequired: true },
    component: () => import("../views/website/events/EventList.vue"),
  },
  {
    path: "/website/events/add",
    name: "Website Event Add",
    meta: { title: "Add Event", authRequired: true },
    component: () => import("../views/website/events/EventForm.vue"),
  },
  {
    path: "/website/events/edit/:id",
    name: "Website Event Edit",
    meta: { title: "Edit Event", authRequired: true },
    component: () => import("../views/website/events/EventForm.vue"),
  },
  {
    path: "/website/contacts",
    name: "Website Contact List",
    meta: { title: "Contact List", authRequired: true },
    component: () => import("../views/website/contacts/ContactReport.vue"),
  },

  // ═══════════════ Documents (Unified) ═══════════════
  {
    path: "/erp/documents",
    name: "ERP Documents",
    meta: { title: "All Documents", authRequired: true },
    component: () => import("../views/erp/documents/List"),
  },
  {
    path: "/erp/documents/expiry",
    name: "ERP Document Expiry",
    meta: { title: "Expiry Dashboard", authRequired: true },
    component: () => import("../views/erp/documents/ExpiryDashboard"),
  },
  {
    path: "/erp/documents/renewal-approvals",
    name: "ERP Document Renewal Approvals",
    meta: { title: "Renewal Approvals", authRequired: true },
    component: () => import("../views/erp/documents/RenewalApprovals"),
  },
  {
    path: "/erp/document-types",
    name: "ERP Document Types",
    meta: { title: "Document Types", authRequired: true },
    component: () => import("../views/erp/documents/TypeList"),
  },
  {
    path: "/erp/document-field-mappings",
    name: "ERP Document Field Mappings",
    meta: { title: "Field Mappings", authRequired: true },
    component: () => import("../views/erp/documents/FieldMappings"),
  },

  // ═══════════════ Transport ═══════════════
  // ---- TP Masters ----
  {
    path: "/erp/tp/masters/vehicle-types",
    name: "ERP TP Vehicle Types",
    meta: { title: "Vehicle Types", authRequired: true },
    component: () => import("../views/erp/transport/masters/VehicleTypeList"),
  },
  {
    path: "/erp/tp/masters/vehicle-makes",
    name: "ERP TP Vehicle Makes",
    meta: { title: "Vehicle Makes", authRequired: true },
    component: () => import("../views/erp/transport/masters/VehicleMakeList"),
  },
  {
    path: "/erp/tp/masters/vehicle-models",
    name: "ERP TP Vehicle Models",
    meta: { title: "Vehicle Models", authRequired: true },
    component: () => import("../views/erp/transport/masters/VehicleModelList"),
  },
  {
    path: "/erp/tp/masters/body-types",
    name: "ERP TP Body Types",
    meta: { title: "Body Types", authRequired: true },
    component: () => import("../views/erp/transport/masters/BodyTypeList"),
  },
  {
    path: "/erp/tp/masters/load-types",
    name: "ERP TP Load Types",
    meta: { title: "Load Types", authRequired: true },
    component: () => import("../views/erp/transport/masters/LoadTypeList"),
  },
  {
    path: "/erp/tp/masters/goods-types",
    name: "ERP TP Goods Types",
    meta: { title: "Goods Types", authRequired: true },
    component: () => import("../views/erp/transport/masters/GoodsTypeList"),
  },
  {
    path: "/erp/tp/masters/packaging-types",
    name: "ERP TP Packaging Types",
    meta: { title: "Packaging Types", authRequired: true },
    component: () => import("../views/erp/transport/masters/PackagingTypeList"),
  },
  {
    path: "/erp/tp/masters/expense-heads",
    name: "ERP TP Expense Heads",
    meta: { title: "Expense Heads", authRequired: true },
    component: () => import("../views/erp/transport/masters/ExpenseHeadList"),
  },
  {
    path: "/erp/tp/masters/tyre-brands",
    name: "ERP TP Tyre Brands",
    meta: { title: "Tyre Brands", authRequired: true },
    component: () => import("../views/erp/transport/masters/TyreBrandList"),
  },
  {
    path: "/erp/tp/masters/complaint-types",
    name: "ERP TP Complaint Types",
    meta: { title: "Complaint Types", authRequired: true },
    component: () => import("../views/erp/transport/masters/ComplaintTypeList"),
  },
  {
    path: "/erp/tp/masters/deduction-reasons",
    name: "ERP TP Deduction Reasons",
    meta: { title: "Deduction Reasons", authRequired: true },
    component: () => import("../views/erp/transport/masters/DeductionReasonList"),
  },
  {
    path: "/erp/tp/masters/trip-expense-types",
    name: "ERP TP Trip Expense Types",
    meta: { title: "Trip Expense Types", authRequired: true },
    component: () => import("../views/erp/transport/masters/TripExpenseTypeList"),
  },
  {
    path: "/erp/tp/masters/charge-types",
    name: "ERP TP Charge Types",
    meta: { title: "Charge Types", authRequired: true },
    component: () => import("../views/erp/transport/masters/ChargeTypeList"),
  },
  {
    path: "/erp/tp/masters/cancellation-reasons",
    name: "ERP TP Cancellation Reasons",
    meta: { title: "Cancellation Reasons", authRequired: true },
    component: () => import("../views/erp/transport/masters/CancellationReasonList"),
  },
  {
    path: "/erp/tp/masters/maintenance-heads",
    name: "ERP TP Maintenance Heads",
    meta: { title: "Maintenance Heads", authRequired: true },
    component: () => import("../views/erp/transport/masters/MaintenanceHeadList"),
  },
  {
    path: "/erp/tp/masters/energy-types",
    name: "ERP TP Energy Types",
    meta: { title: "Energy Types", authRequired: true },
    component: () => import("../views/erp/transport/masters/EnergyTypeList"),
  },
  {
    path: "/erp/tp/masters/system-settings",
    name: "ERP TP System Settings",
    meta: { title: "TP System Settings", authRequired: true },
    component: () => import("../views/erp/transport/masters/SystemSettings"),
  },

  // ---- TP Vehicles ----
  {
    path: "/erp/tp/vehicles",
    name: "ERP TP Vehicles",
    meta: { title: "Vehicles", authRequired: true },
    component: () => import("../views/erp/transport/vehicles/List"),
  },
  {
    path: "/erp/tp/vehicles/create",
    name: "ERP TP Vehicle Create",
    meta: { title: "Create Vehicle", authRequired: true },
    component: () => import("../views/erp/transport/vehicles/Form"),
  },
  {
    path: "/erp/tp/vehicles/:id",
    name: "ERP TP Vehicle Edit",
    meta: { title: "Edit Vehicle", authRequired: true },
    component: () => import("../views/erp/transport/vehicles/Form"),
  },
  {
    path: "/erp/tp/vehicles/:id/details",
    name: "ERP TP Vehicle Details",
    meta: { title: "Vehicle Details", authRequired: true },
    component: () => import("../views/erp/transport/vehicles/Details"),
  },

  // ---- TP Vehicle Loans (EMI Management) ----
  {
    path: "/erp/tp/vehicle-loans",
    name: "ERP TP Vehicle Loans List",
    meta: { title: "Vehicle Loans", authRequired: true },
    component: () => import("../views/erp/transport/vehicle-loans/List.vue"),
  },
  {
    path: "/erp/tp/vehicle-loans/create",
    name: "ERP TP Vehicle Loan Create",
    meta: { title: "New Vehicle Loan", authRequired: true },
    component: () => import("../views/erp/transport/vehicle-loans/Form.vue"),
  },
  {
    path: "/erp/tp/vehicle-loans/:id",
    name: "ERP TP Vehicle Loan Details",
    meta: { title: "Vehicle Loan", authRequired: true },
    component: () => import("../views/erp/transport/vehicle-loans/Details.vue"),
  },
  {
    path: "/erp/tp/vehicle-loans/:id/edit",
    name: "ERP TP Vehicle Loan Edit",
    meta: { title: "Edit Vehicle Loan", authRequired: true },
    component: () => import("../views/erp/transport/vehicle-loans/Form.vue"),
  },

  // ---- TP Vehicle Owners ----
  {
    path: "/erp/tp/vehicle-owners",
    name: "ERP TP Vehicle Owners",
    meta: { title: "Vehicle Owners", authRequired: true },
    component: () => import("../views/erp/transport/vehicle-owners/List"),
  },
  {
    path: "/erp/tp/vehicle-owners/create",
    name: "ERP TP Vehicle Owner Create",
    meta: { title: "Create Vehicle Owner", authRequired: true },
    component: () => import("../views/erp/transport/vehicle-owners/Form"),
  },
  {
    path: "/erp/tp/vehicle-owners/:id",
    name: "ERP TP Vehicle Owner Edit",
    meta: { title: "Edit Vehicle Owner", authRequired: true },
    component: () => import("../views/erp/transport/vehicle-owners/Form"),
  },
  {
    path: "/erp/tp/vehicle-owners/:id/details",
    name: "ERP TP Vehicle Owner Details",
    meta: { title: "Vehicle Owner Details", authRequired: true },
    component: () => import("../views/erp/transport/vehicle-owners/Details"),
  },

  // ---- TP Drivers ----
  {
    path: "/erp/tp/drivers",
    name: "ERP TP Drivers",
    meta: { title: "Drivers", authRequired: true },
    component: () => import("../views/erp/transport/drivers/List"),
  },
  {
    path: "/erp/tp/drivers/create",
    name: "ERP TP Driver Create",
    meta: { title: "Create Driver", authRequired: true },
    component: () => import("../views/erp/transport/drivers/Form"),
  },
  {
    path: "/erp/tp/drivers/:id",
    name: "ERP TP Driver Edit",
    meta: { title: "Edit Driver", authRequired: true },
    component: () => import("../views/erp/transport/drivers/Form"),
  },
  {
    path: "/erp/tp/drivers/:id/details",
    name: "ERP TP Driver Details",
    meta: { title: "Driver Details", authRequired: true },
    component: () => import("../views/erp/transport/drivers/Details"),
  },

  // Routes & Hubs
  { path: "/erp/tp/routes", name: "ERP TP Routes", meta: { title: "Routes", authRequired: true }, component: () => import("../views/erp/transport/routes/List") },
  { path: "/erp/tp/routes/create", name: "ERP TP Route Create", meta: { title: "Create Route", authRequired: true }, component: () => import("../views/erp/transport/routes/Form") },
  { path: "/erp/tp/routes/:id", name: "ERP TP Route Edit", meta: { title: "Edit Route", authRequired: true }, component: () => import("../views/erp/transport/routes/Form") },
  { path: "/erp/tp/routes/:id/details", name: "ERP TP Route Details", meta: { title: "Route Details", authRequired: true }, component: () => import("../views/erp/transport/routes/Details") },
  { path: "/erp/tp/hubs", name: "ERP TP Hubs", meta: { title: "Hubs", authRequired: true }, component: () => import("../views/erp/transport/hubs/List") },
  // Customers & Rates
  { path: "/erp/tp/customers", name: "ERP TP Customers", meta: { title: "Customers", authRequired: true }, component: () => import("../views/erp/transport/customers/List") },
  { path: "/erp/tp/customers/create", name: "ERP TP Customer Create", meta: { title: "Create Customer", authRequired: true }, component: () => import("../views/erp/transport/customers/Form") },
  { path: "/erp/tp/customers/:id", name: "ERP TP Customer Edit", meta: { title: "Edit Customer", authRequired: true }, component: () => import("../views/erp/transport/customers/Form") },
  { path: "/erp/tp/customers/:id/details", name: "ERP TP Customer Details", meta: { title: "Customer Details", authRequired: true }, component: () => import("../views/erp/transport/customers/Details") },
  { path: "/erp/tp/brokers", name: "ERP TP Brokers", meta: { title: "Brokers", authRequired: true }, component: () => import("../views/erp/transport/brokers/List") },
  { path: "/erp/tp/rate-agreements", name: "ERP TP Rate Agreements", meta: { title: "Rate Agreements", authRequired: true }, component: () => import("../views/erp/transport/rate-agreements/List") },
  { path: "/erp/tp/rate-agreements/create", name: "ERP TP Rate Agreement Create", meta: { title: "Create Rate Agreement", authRequired: true }, component: () => import("../views/erp/transport/rate-agreements/Form") },
  { path: "/erp/tp/rate-agreements/:id", name: "ERP TP Rate Agreement Edit", meta: { title: "Edit Rate Agreement", authRequired: true }, component: () => import("../views/erp/transport/rate-agreements/Form") },
  { path: "/erp/tp/rate-agreements/:id/details", name: "ERP TP Rate Agreement Details", meta: { title: "Rate Agreement Details", authRequired: true }, component: () => import("../views/erp/transport/rate-agreements/Details") },

  // ---- TP Maintenance ----
  { path: "/erp/tp/maintenance", name: "ERP TP Maintenance", meta: { title: "Maintenance", authRequired: true }, component: () => import("../views/erp/transport/maintenance/List") },
  { path: "/erp/tp/maintenance/create", name: "ERP TP Maintenance Create", meta: { title: "Create Maintenance", authRequired: true }, component: () => import("../views/erp/transport/maintenance/Form") },
  { path: "/erp/tp/maintenance/:id", name: "ERP TP Maintenance Edit", meta: { title: "Edit Maintenance", authRequired: true }, component: () => import("../views/erp/transport/maintenance/Form") },

  // ---- TP Fuel ----
  { path: "/erp/tp/fuel", name: "ERP TP Fuel", meta: { title: "Fuel Records", authRequired: true }, component: () => import("../views/erp/transport/fuel/List") },
  { path: "/erp/tp/fuel/create", name: "ERP TP Fuel Create", meta: { title: "Create Fuel Record", authRequired: true }, component: () => import("../views/erp/transport/fuel/Form") },
  { path: "/erp/tp/fuel/:id", name: "ERP TP Fuel Edit", meta: { title: "Edit Fuel Record", authRequired: true }, component: () => import("../views/erp/transport/fuel/Form") },

  // -- Transport: Hub Arrivals / Driver Console --
  // Dispatch wizard removed — CN POST auto-creates the trip + flips vehicle state.
  { path: "/erp/tp/hub-arrivals", name: "ERP TP Hub Arrivals", meta: { title: "Hub Arrivals", authRequired: true }, component: () => import("../views/erp/transport/dispatch/HubArrivals.vue") },
  { path: "/erp/tp/driver-console", name: "ERP TP Driver Console", meta: { title: "Driver Console", authRequired: true }, component: () => import("../views/erp/transport/driver-console/Index.vue") },

  // -- Transport: Consignment Notes --
  { path: "/erp/tp/consignment-notes", name: "ERP TP CN List", meta: { title: "Consignment Notes", authRequired: true }, component: () => import("../views/erp/transport/consignment-notes/List") },
  { path: "/erp/tp/consignment-notes/create", name: "ERP TP CN Create", meta: { title: "Create Consignment Note", authRequired: true }, component: () => import("../views/erp/transport/consignment-notes/Form") },
  { path: "/erp/tp/consignment-notes/:id", name: "ERP TP CN Edit", meta: { title: "Edit Consignment Note", authRequired: true }, component: () => import("../views/erp/transport/consignment-notes/Form") },
  { path: "/erp/tp/consignment-notes/:id/details", name: "ERP TP CN Details", meta: { title: "CN Details", authRequired: true }, component: () => import("../views/erp/transport/consignment-notes/Details") },
  // Phase 5w4 — redirect to the unified print route.
  { path: "/erp/tp/consignment-notes/:id/print", name: "ERP TP CN Print", redirect: (to) => `/print/ConsignmentNote/${to.params.id}?autoprint=1` },
  { path: "/erp/tp/consignment-notes/:id/print-lr", name: "ERP TP CN Print LR", meta: { title: "Print LR (Builty)", authRequired: true }, component: () => import("../views/erp/transport/consignment-notes/PrintLR.vue") },
  { path: "/erp/tp/consignment-notes/:id/print-challan", name: "ERP TP CN Print Challan", meta: { title: "Print Freight Challan", authRequired: true }, component: () => import("../views/erp/transport/consignment-notes/PrintChallan.vue") },

  // -- Transport: Trip Sheets --
  { path: "/erp/tp/trip-sheets", name: "ERP TP Trip List", meta: { title: "Trip Sheets", authRequired: true }, component: () => import("../views/erp/transport/trip-sheets/List") },
  { path: "/erp/tp/trip-sheets/create", name: "ERP TP Trip Create", meta: { title: "Create Trip Sheet", authRequired: true }, component: () => import("../views/erp/transport/trip-sheets/Form") },
  { path: "/erp/tp/trip-sheets/:id", name: "ERP TP Trip Edit", meta: { title: "Edit Trip Sheet", authRequired: true }, component: () => import("../views/erp/transport/trip-sheets/Form") },
  { path: "/erp/tp/trip-sheets/:id/details", name: "ERP TP Trip Details", meta: { title: "Trip Details", authRequired: true }, component: () => import("../views/erp/transport/trip-sheets/Details") },

  // -- Transport: Freight Invoices --
  { path: "/erp/tp/freight-invoices", name: "ERP TP FI List", meta: { title: "Freight Invoices", authRequired: true }, component: () => import("../views/erp/transport/freight-invoices/List") },
  { path: "/erp/tp/freight-invoices/create", name: "ERP TP FI Create", meta: { title: "Create Freight Invoice", authRequired: true }, component: () => import("../views/erp/transport/freight-invoices/Form") },
  { path: "/erp/tp/freight-invoices/:id/edit", name: "ERP TP FI Edit", meta: { title: "Edit Freight Invoice", authRequired: true }, component: () => import("../views/erp/transport/freight-invoices/Form") },
  { path: "/erp/tp/freight-invoices/:id/details", name: "ERP TP FI Details", meta: { title: "Freight Invoice Details", authRequired: true }, component: () => import("../views/erp/transport/freight-invoices/Details") },
  { path: "/erp/tp/freight-invoices/:id/print", name: "ERP TP FI Print", meta: { title: "Freight Invoice Print", authRequired: true }, component: () => import("../views/erp/transport/freight-invoices/Print") },
  { path: "/erp/tp/vehicle-owner-invoices", name: "ERP TP VOI List", meta: { title: "Vehicle Owner Invoices", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-invoices/List") },
  { path: "/erp/tp/vehicle-owner-invoices/create", name: "ERP TP VOI Create", meta: { title: "Create Vehicle Owner Invoice", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-invoices/Form") },
  { path: "/erp/tp/vehicle-owner-invoices/:id/edit", name: "ERP TP VOI Edit", meta: { title: "Edit Vehicle Owner Invoice", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-invoices/Form") },
  { path: "/erp/tp/vehicle-owner-invoices/:id/details", name: "ERP TP VOI Details", meta: { title: "Vehicle Owner Invoice Details", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-invoices/Details") },
  { path: "/erp/tp/vehicle-owner-invoices/:id/print", name: "ERP TP VOI Print", meta: { title: "Vehicle Owner Invoice Print", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-invoices/Print") },

  // -- Transport: Vehicle Owner Payments (TO Payments) --
  { path: "/erp/tp/vehicle-owner-payments", name: "ERP TP VOP List", meta: { title: "TO Payments", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-payments/List.vue") },
  { path: "/erp/tp/vehicle-owner-payments/create", name: "ERP TP VOP Create", meta: { title: "Create TO Payment", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-payments/Form.vue") },
  { path: "/erp/tp/vehicle-owner-payments/:id/edit", name: "ERP TP VOP Edit", meta: { title: "Edit TO Payment", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-payments/Form.vue") },
  { path: "/erp/tp/vehicle-owner-payments/:id/details", name: "ERP TP VOP Details", meta: { title: "TO Payment Details", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-payments/Details.vue") },
  { path: "/erp/tp/vehicle-owner-payments/:id/print", name: "ERP TP VOP Print", meta: { title: "TO Payment Print", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-payments/Print.vue") },
  { path: "/erp/tp/reports/to-payment-register", name: "ERP TP TO Payment Register", meta: { title: "TO Payment Register", authRequired: true }, component: () => import("../views/erp/transport/reports/ToPaymentRegister.vue") },

  // -- Transport: Payments Landing + Bill/Fuel Payment modules --
  { path: "/erp/tp/payments-landing", name: "ERP TP Payments Landing", meta: { title: "Payments Landing", authRequired: true }, component: () => import("../views/erp/transport/payments-landing/Index.vue") },
  { path: "/erp/tp/vehicle-owner-bill-payments", name: "ERP TP VOBP List", meta: { title: "Owner Bill Payments", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-bill-payments/List.vue") },
  { path: "/erp/tp/vehicle-owner-bill-payments/create", name: "ERP TP VOBP Create", meta: { title: "Create Owner Bill Payment", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-bill-payments/Form.vue") },
  { path: "/erp/tp/vehicle-owner-bill-payments/:id/edit", name: "ERP TP VOBP Edit", meta: { title: "Edit Owner Bill Payment", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-bill-payments/Form.vue") },
  { path: "/erp/tp/vehicle-owner-bill-payments/:id/details", name: "ERP TP VOBP Details", meta: { title: "Owner Bill Payment Details", authRequired: true }, component: () => import("../views/erp/transport/vehicle-owner-bill-payments/Details.vue") },
  { path: "/erp/tp/fuel-bill-payments", name: "ERP TP FBP List", meta: { title: "Diesel Bill Payments", authRequired: true }, component: () => import("../views/erp/transport/fuel-bill-payments/List.vue") },
  { path: "/erp/tp/fuel-bill-payments/create", name: "ERP TP FBP Create", meta: { title: "Create Diesel Bill Payment", authRequired: true }, component: () => import("../views/erp/transport/fuel-bill-payments/Form.vue") },
  { path: "/erp/tp/fuel-bill-payments/:id/edit", name: "ERP TP FBP Edit", meta: { title: "Edit Diesel Bill Payment", authRequired: true }, component: () => import("../views/erp/transport/fuel-bill-payments/Form.vue") },
  { path: "/erp/tp/fuel-bill-payments/:id/details", name: "ERP TP FBP Details", meta: { title: "Diesel Bill Payment Details", authRequired: true }, component: () => import("../views/erp/transport/fuel-bill-payments/Details.vue") },

  // -- Transport: Diesel Bills (Pump Statement Reconciliation) --
  { path: "/erp/tp/fuel-bills", name: "ERP TP FB List", meta: { title: "Diesel Bills", authRequired: true }, component: () => import("../views/erp/transport/fuel-bills/List") },
  { path: "/erp/tp/fuel-bills/create", name: "ERP TP FB Create", meta: { title: "New Diesel Bill", authRequired: true }, component: () => import("../views/erp/transport/fuel-bills/Form") },
  { path: "/erp/tp/fuel-bills/:id", name: "ERP TP FB Details", meta: { title: "Diesel Bill Details", authRequired: true }, component: () => import("../views/erp/transport/fuel-bills/Details") },
  { path: "/erp/tp/fuel-bills/:id/edit", name: "ERP TP FB Edit", meta: { title: "Edit Diesel Bill", authRequired: true }, component: () => import("../views/erp/transport/fuel-bills/Form") },

  // -- Transport: Diesel Adjustments (Post-Close Variance) --
  { path: "/erp/tp/fuel-bill-adjustments", name: "ERP TP FBA List", meta: { title: "Diesel Adjustments", authRequired: true }, component: () => import("../views/erp/transport/fuel-bill-adjustments/List.vue") },

  // -- Transport: Hire Charges --
  { path: "/erp/tp/hire-charges", name: "ERP TP HC List", meta: { title: "Hire Charges", authRequired: true }, component: () => import("../views/erp/transport/hire-charges/List") },
  { path: "/erp/tp/hire-charges/create", name: "ERP TP HC Create", meta: { title: "Create Hire Charge", authRequired: true }, component: () => import("../views/erp/transport/hire-charges/Form") },
  { path: "/erp/tp/hire-charges/:id", name: "ERP TP HC Edit", meta: { title: "Edit Hire Charge", authRequired: true }, component: () => import("../views/erp/transport/hire-charges/Form") },

  // -- Transport: Driver Settlements --
  { path: "/erp/tp/driver-settlements", name: "ERP TP DS List", meta: { title: "Driver Settlements", authRequired: true }, component: () => import("../views/erp/transport/driver-settlements/List") },
  { path: "/erp/tp/driver-settlements/:id/details", name: "ERP TP DS Details", meta: { title: "Settlement Details", authRequired: true }, component: () => import("../views/erp/transport/driver-settlements/Details") },
  { path: "/erp/tp/driver-salaries", name: "ERP TP Driver Salaries", meta: { title: "Driver Salaries", authRequired: true }, component: () => import("../views/erp/transport/driver-salaries/List") },
  { path: "/erp/tp/salary-periods", name: "ERP TP Salary Periods", meta: { title: "Salary Periods", authRequired: true }, component: () => import("../views/erp/transport/salary-periods/List") },
  { path: "/erp/tp/salary-periods/new", name: "ERP TP Salary Period New", meta: { title: "Create Salary Period", authRequired: true }, component: () => import("../views/erp/transport/salary-periods/Form") },
  { path: "/erp/tp/salary-periods/:id", name: "ERP TP Salary Period Edit", meta: { title: "Edit Salary Period", authRequired: true }, component: () => import("../views/erp/transport/salary-periods/Form") },
  { path: "/erp/tp/salary-periods/:id/details", name: "ERP TP Salary Period Detail", meta: { title: "Salary Period Detail", authRequired: true }, component: () => import("../views/erp/transport/salary-periods/Details") },

  // -- Transport: Warehouse --
  { path: "/erp/tp/warehouse/inward", name: "ERP TP WH Inward List", meta: { title: "Warehouse Inward", authRequired: true }, component: () => import("../views/erp/transport/warehouse/InwardList") },
  { path: "/erp/tp/warehouse/inward/create", name: "ERP TP WH Inward Create", meta: { title: "Create Inward", authRequired: true }, component: () => import("../views/erp/transport/warehouse/InwardForm") },
  { path: "/erp/tp/warehouse/outward", name: "ERP TP WH Outward List", meta: { title: "Warehouse Outward", authRequired: true }, component: () => import("../views/erp/transport/warehouse/OutwardList") },
  { path: "/erp/tp/warehouse/outward/create", name: "ERP TP WH Outward Create", meta: { title: "Create Outward", authRequired: true }, component: () => import("../views/erp/transport/warehouse/OutwardForm") },
  { path: "/erp/tp/warehouse/transit-inventory", name: "ERP TP WH Transit", meta: { title: "Transit Inventory", authRequired: true }, component: () => import("../views/erp/transport/warehouse/TransitInventory") },

  // -- Transport: CRM --
  { path: "/erp/tp/crm/leads", name: "ERP TP Lead List", meta: { title: "Leads", authRequired: true }, component: () => import("../views/erp/transport/crm/LeadList") },
  { path: "/erp/tp/crm/leads/create", name: "ERP TP Lead Create", meta: { title: "Create Lead", authRequired: true }, component: () => import("../views/erp/transport/crm/LeadForm") },
  { path: "/erp/tp/crm/leads/:id", name: "ERP TP Lead Edit", meta: { title: "Edit Lead", authRequired: true }, component: () => import("../views/erp/transport/crm/LeadForm") },
  { path: "/erp/tp/crm/complaints", name: "ERP TP Complaint List", meta: { title: "Complaints", authRequired: true }, component: () => import("../views/erp/transport/crm/ComplaintList") },
  { path: "/erp/tp/crm/complaints/create", name: "ERP TP Complaint Create", meta: { title: "Create Complaint", authRequired: true }, component: () => import("../views/erp/transport/crm/ComplaintForm") },
  { path: "/erp/tp/crm/complaints/:id", name: "ERP TP Complaint Edit", meta: { title: "Edit Complaint", authRequired: true }, component: () => import("../views/erp/transport/crm/ComplaintForm") },

  // -- Transport: Tyres --
  { path: "/erp/tp/tyres", name: "ERP TP Tyre List", meta: { title: "Tyres", authRequired: true }, component: () => import("../views/erp/transport/tyres/List") },
  { path: "/erp/tp/tyres/create", name: "ERP TP Tyre Create", meta: { title: "New Tyre", authRequired: true }, component: () => import("../views/erp/transport/tyres/Form") },
  { path: "/erp/tp/tyres/:id/details", name: "ERP TP Tyre Details", meta: { title: "Tyre Details", authRequired: true }, component: () => import("../views/erp/transport/tyres/Details") },

  // -- Transport: Batteries --
  { path: "/erp/tp/batteries", name: "ERP TP Battery List", meta: { title: "Batteries", authRequired: true }, component: () => import("../views/erp/transport/batteries/List") },
  { path: "/erp/tp/batteries/create", name: "ERP TP Battery Create", meta: { title: "New Battery", authRequired: true }, component: () => import("../views/erp/transport/batteries/Form") },
  { path: "/erp/tp/batteries/:id/details", name: "ERP TP Battery Details", meta: { title: "Battery Details", authRequired: true }, component: () => import("../views/erp/transport/batteries/Details") },

  // -- Transport: Vehicle Inspections --
  { path: "/erp/tp/inspections", name: "ERP TP Inspection List", meta: { title: "Vehicle Inspections", authRequired: true }, component: () => import("../views/erp/transport/inspections/List") },
  { path: "/erp/tp/inspections/create", name: "ERP TP Inspection Create", meta: { title: "New Inspection", authRequired: true }, component: () => import("../views/erp/transport/inspections/Form") },
  { path: "/erp/tp/inspections/:id/details", name: "ERP TP Inspection Details", meta: { title: "Inspection Details", authRequired: true }, component: () => import("../views/erp/transport/inspections/Details") },

  // -- Transport: Dashboard --
  { path: "/erp/tp/dashboard/admin", name: "ERP TP Admin Dashboard", meta: { title: "Transport Dashboard", authRequired: true }, component: () => import("../views/erp/transport/dashboard/AdminDashboard") },
  { path: "/erp/tp/dashboard/fleet", name: "ERP TP Fleet Dashboard", meta: { title: "Fleet Dashboard", authRequired: true }, component: () => import("../views/erp/transport/dashboard/FleetDashboard") },
  { path: "/erp/tp/fleet-status", name: "ERP TP Fleet Status Board", meta: { title: "Fleet Status Board", authRequired: true }, component: () => import("../views/erp/transport/fleet-status/Board") },

  // -- Transport: Reports --
  { path: "/erp/tp/reports/fleet-utilization", name: "ERP TP Fleet Utilization", meta: { title: "Fleet Utilization", authRequired: true }, component: () => import("../views/erp/transport/reports/FleetUtilizationReport") },
  { path: "/erp/tp/reports/consignment-register", name: "ERP TP CN Register", meta: { title: "CN Register", authRequired: true }, component: () => import("../views/erp/transport/reports/ConsignmentRegisterReport") },
  { path: "/erp/tp/reports/trip-register", name: "ERP TP Trip Register", meta: { title: "Trip Register", authRequired: true }, component: () => import("../views/erp/transport/reports/TripRegisterReport") },
  { path: "/erp/tp/reports/delivery-performance", name: "ERP TP Delivery Perf", meta: { title: "Delivery Performance", authRequired: true }, component: () => import("../views/erp/transport/reports/DeliveryPerformanceReport") },
  { path: "/erp/tp/reports/fuel-consumption", name: "ERP TP Fuel Report", meta: { title: "Fuel Consumption", authRequired: true }, component: () => import("../views/erp/transport/reports/FuelConsumptionReport") },
  { path: "/erp/tp/reports/revenue-by-customer", name: "ERP TP Rev Customer", meta: { title: "Revenue by Customer", authRequired: true }, component: () => import("../views/erp/transport/reports/RevenueByCustomerReport") },
  { path: "/erp/tp/reports/revenue-by-route", name: "ERP TP Rev Route", meta: { title: "Revenue by Route", authRequired: true }, component: () => import("../views/erp/transport/reports/RevenueByRouteReport") },
  { path: "/erp/tp/reports/outstanding-freight", name: "ERP TP Outstanding", meta: { title: "Outstanding Freight", authRequired: true }, component: () => import("../views/erp/transport/reports/OutstandingFreightReport") },
  { path: "/erp/tp/reports/unclosed-cn", name: "ERP TP Unclosed CN", meta: { title: "Unclosed CN Closing", authRequired: true }, component: () => import("../views/erp/transport/reports/UnclosedCnSummary") },
  { path: "/erp/tp/reports/driver-performance", name: "ERP TP Driver Perf", meta: { title: "Driver Performance", authRequired: true }, component: () => import("../views/erp/transport/reports/DriverPerformanceReport") },
  { path: "/erp/tp/reports/vehicle-expense", name: "ERP TP Vehicle Expense", meta: { title: "Vehicle Expense", authRequired: true }, component: () => import("../views/erp/transport/reports/VehicleExpenseReport") },
  { path: "/erp/tp/reports/tyre-life", name: "ERP TP Tyre Life", meta: { title: "Tyre Life Report", authRequired: true }, component: () => import("../views/erp/transport/reports/TyreLifeReport") },
  { path: "/erp/tp/reports/pending-pod", name: "ERP TP Pending POD", meta: { title: "Pending POD", authRequired: true }, component: () => import("../views/erp/transport/reports/PendingPodReport") },
  { path: "/erp/tp/reports/trip-pnl", name: "ERP TP Trip PnL", meta: { title: "Trip P&L Report", authRequired: true }, component: () => import("../views/erp/transport/reports/TripPnlReport") },
  { path: "/erp/tp/reports/empty-running", name: "ERP TP Empty Running", meta: { title: "Empty Running Report", authRequired: true }, component: () => import("../views/erp/transport/reports/EmptyRunningReport") },
  { path: "/erp/tp/reports/margin", name: "ERP TP Margin Analysis", meta: { title: "Margin Analysis", authRequired: true }, component: () => import("../views/erp/transport/reports/MarginReport") },
  { path: "/erp/tp/reports/owner-ledger", name: "ERP TP Owner Ledger", meta: { title: "Vehicle Owner Ledger", authRequired: true }, component: () => import("../views/erp/transport/reports/VehicleOwnerLedger") },
  { path: "/erp/tp/reports/truck-owner-settlement", name: "ERP TP Truck Owner Settlement", meta: { title: "Truck Owner Settlement", authRequired: true }, component: () => import("../views/erp/transport/reports/TruckOwnerSettlement.vue") },
  { path: "/erp/tp/reports/customer-settlement", name: "ERP TP Customer Settlement", meta: { title: "Customer Settlement", authRequired: true }, component: () => import("../views/erp/transport/reports/CustomerSettlement.vue") },

  // ---- Mobile Supervisor Routes ----
  {
    path: "/mobile/login",
    name: "mobile-login",
    component: () => import("../views/mobile/supervisor/Login.vue"),
    meta: { title: "Mobile Login" },
  },
  {
    path: "/mobile",
    component: () => import("../layouts/MobileLayout.vue"),
    meta: { authRequired: true, loginRoute: "/mobile/login" },
    children: [
      {
        path: "dashboard",
        name: "mobile-dashboard",
        component: () => import("../views/mobile/supervisor/Dashboard.vue"),
        meta: { title: "Supervisor Dashboard", authRequired: true, loginRoute: "/mobile/login" },
      },
      {
        path: "stock",
        name: "mobile-stock-summary",
        component: () => import("../views/mobile/supervisor/StockSummary.vue"),
        meta: { title: "Stock Summary", authRequired: true, loginRoute: "/mobile/login" },
      },
      {
        path: ":type/:id",
        name: "mobile-program-detail",
        component: () => import("../views/mobile/supervisor/ProgramDetail.vue"),
        meta: { title: "Program Detail", authRequired: true, loginRoute: "/mobile/login" },
        props: true,
      },
      {
        path: ":type/:id/line/:lineId",
        name: "mobile-enter-actuals",
        component: () => import("../views/mobile/supervisor/EnterActuals.vue"),
        meta: { title: "Enter Actuals", authRequired: true, loginRoute: "/mobile/login" },
        props: true,
      },
      {
        path: "manufacturing/:id",
        name: "mobile-manufacturing-detail",
        component: () => import("../views/mobile/supervisor/ManufacturingDetail.vue"),
        meta: { title: "Manufacturing Detail", authRequired: true, loginRoute: "/mobile/login" },
        props: true,
      },
      {
        path: "manufacturing/:id/consume/:lineId",
        name: "mobile-scan-consumption",
        component: () => import("../views/mobile/supervisor/ScanConsumption.vue"),
        meta: { title: "Scan Consumption", authRequired: true, loginRoute: "/mobile/login" },
        props: true,
      },
      {
        path: "manufacturing/:id/produce/:lineId",
        name: "mobile-enter-production",
        component: () => import("../views/mobile/supervisor/EnterProduction.vue"),
        meta: { title: "Enter Production", authRequired: true, loginRoute: "/mobile/login" },
        props: true,
      },
    ],
  },

  // ── Gate User Mobile ──────────────────────────────────────────
  {
    path: "/mobile/gate",
    component: () => import("../layouts/MobileLayout.vue"),
    meta: { authRequired: true, loginRoute: "/mobile/login" },
    children: [
      {
        path: "dashboard",
        name: "mobile-gate-dashboard",
        component: () => import("../views/mobile/gate/GateDashboard.vue"),
        meta: { title: "Gate Entry", authRequired: true, loginRoute: "/mobile/login" },
      },
      {
        path: ":type/:id",
        name: "mobile-gate-entry-form",
        component: () => import("../views/mobile/gate/GateEntryForm.vue"),
        meta: { title: "Gate Entry Form", authRequired: true, loginRoute: "/mobile/login" },
        props: true,
      },
      {
        path: ":type/:id/success",
        name: "mobile-gate-entry-success",
        component: () => import("../views/mobile/gate/GateEntrySuccess.vue"),
        meta: { title: "Gate Entry Complete", authRequired: true, loginRoute: "/mobile/login" },
        props: true,
      },
    ],
  },
  // ─── Print Settings (per-company print profile editor) ───
  {
    path: "/erp/print-settings",
    name: "PrintSettings",
    meta: { title: "Print Settings", authRequired: true },
    component: () => import("../views/erp/print-settings/Form.vue"),
  },
  // ─── Internal print route (server-side Chromium renderer target) ───
  // Loaded by PrintRenderingService.cs inside a headless browser. Strips the
  // app shell, reads cookies for auth, dispatches to the right template by
  // `type`, and signals readiness via `window.__printReady = true`. The same
  // route also serves as the daily user-facing fast-print path when opened
  // in a regular tab — the action bar shows on screen, hides via
  // `@media print`.
  {
    path: "/print/internal/:type/:id",
    name: "PrintInternal",
    meta: { title: "Print", authRequired: true },
    component: () => import("../views/print/InternalPrintHost.vue"),
    props: true,
  },
  {
    // Convenience alias — opens the same route at a shorter URL for the
    // user-facing button. `embed=1` query strips the action bar.
    path: "/print/:type/:id",
    name: "PrintPublic",
    meta: { title: "Print", authRequired: true },
    component: () => import("../views/print/InternalPrintHost.vue"),
    props: true,
  },
  // ─── Dev-only print gallery ───
  // Renders one mock record per print type stacked side-by-side. Used during
  // Phase 2-4 to visually verify the unified print system with no live data.
  // Gated by NODE_ENV in the route guard.
  {
    path: "/dev/print-gallery",
    name: "DevPrintGallery",
    meta: { title: "Print Gallery (Dev)", authRequired: true },
    component: () => import("../views/dev/PrintGallery.vue"),
    beforeEnter(to, from, next) {
      if (process.env.NODE_ENV !== "development") {
        next({ path: "/" });
      } else {
        next();
      }
    },
  },
];
